import type { InvoiceStatus } from './invoiceStatus';

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';

export interface LimitExceededError {
  code: 'limit_exceeded' | 'trial_expired';
  reason: 'limit_exceeded' | 'trial_expired';
  limit: string;
  message: string;
  current: number;
  max: number;
  upgrade_path: string;
  current_plan: string;
}

export interface SendBlockError {
  code: 'email_not_verified' | 'permission_denied_view_only';
  detail: string;
  email?: string;
}

interface ApiResponse<T> {
  data?: T;
  error?: string;
  limitExceeded?: LimitExceededError;
  sendBlocked?: SendBlockError;
  rateLimited?: boolean;
}

// Pagination types for server-side pagination
export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface PaginationParams {
  limit?: number;
  offset?: number;
  ordering?: string;
}

// React Query adapter: throws on error so useQuery can manage error state
export async function queryFn<T>(response: Promise<ApiResponse<T>>): Promise<T> {
  const result = await response;
  if (result.error) throw new Error(result.error);
  if (!result.data) throw new Error('No data');
  return result.data;
}

// Token refresh state management
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

// Decode JWT to get expiration time (without verification)
function getTokenExpiration(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload));
    return decoded.exp ? decoded.exp * 1000 : null; // Convert to milliseconds
  } catch {
    return null;
  }
}

// Check if token is expired or will expire within the buffer time
function isTokenExpiredOrExpiring(token: string, bufferMs = 60000): boolean {
  const exp = getTokenExpiration(token);
  if (!exp) return true;
  return Date.now() >= exp - bufferMs;
}

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    return null;
  }

  // Check if refresh token is also expired
  if (isTokenExpiredOrExpiring(refreshToken, 0)) {
    return null;
  }

  try {
    const response = await fetch(`${API_BASE_URL}/auth/token/refresh/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh: refreshToken }),
    });

    const data = await response.json();

    if (!response.ok) {
      return null;
    }

    // Save new access token
    localStorage.setItem('accessToken', data.access);
    return data.access;
  } catch {
    return null;
  }
}

async function getValidToken(): Promise<string | null> {
  // If already refreshing, wait for that refresh to complete
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  const token = localStorage.getItem('accessToken');

  // If no token, nothing to return
  if (!token) {
    return null;
  }

  // Proactively refresh if token is expired or expiring within 1 minute
  if (isTokenExpiredOrExpiring(token, 60000)) {
    isRefreshing = true;
    refreshPromise = refreshAccessToken();
    const newToken = await refreshPromise;
    isRefreshing = false;
    refreshPromise = null;
    return newToken;
  }

  return token;
}

async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  isRetry = false
): Promise<ApiResponse<T>> {
  const url = `${API_BASE_URL}${endpoint}`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  const token = await getValidToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Handle 204 No Content (e.g., DELETE requests)
    if (response.status === 204) {
      return { data: undefined as T };
    }

    const data = await response.json();

    // Handle 402 Payment Required (limit exceeded)
    if (response.status === 402 && data.code === 'limit_exceeded') {
      return { error: data.message, limitExceeded: data as LimitExceededError };
    }

    // Handle 403 send blocked (email not verified or view_only)
    if (response.status === 403 && (data.code === 'email_not_verified' || data.code === 'permission_denied_view_only')) {
      return { error: data.detail, sendBlocked: data as SendBlockError };
    }

    // Handle 429 rate limited
    if (response.status === 429) {
      return { error: data.detail, rateLimited: true };
    }

    if (!response.ok) {
      // Check for invalid/expired token
      if (data.code === 'token_not_valid' && !isRetry) {
        // Try to refresh token
        if (!isRefreshing) {
          isRefreshing = true;
          refreshPromise = refreshAccessToken();
        }

        const newToken = await refreshPromise;
        isRefreshing = false;
        refreshPromise = null;

        if (newToken) {
          // Retry the original request with new token
          return apiRequest<T>(endpoint, options, true);
        }

        // Refresh failed, clear tokens and redirect
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return { error: 'Session expired. Please login again.' };
      }

      let errorMessage = data.detail || data.email?.[0] || data.password1?.[0] || data.password?.[0] || data.name?.[0];
      if (!errorMessage) {
        const firstArray = Object.values(data).find((v): v is string[] => Array.isArray(v) && typeof v[0] === 'string');
        errorMessage = firstArray?.[0] || 'An error occurred';
      }
      return { error: errorMessage };
    }

    return { data };
  } catch {
    return { error: 'Network error. Please try again.' };
  }
}

export interface Business {
  id: string;
  name: string;
  address: string;
  address_latitude?: number;
  address_longitude?: number;
  timezone?: string;
  industry: string;
  industry_other: string;
  logo: string;
  phone: string;
  website: string;
  initials: string;
  created_at: string;
  default_stripe_card_enabled: boolean;
  default_stripe_ach_enabled: boolean;
  default_currency: string;
  currency_code: string;
  client_reviews_enabled: boolean;
  auto_send_receipt: boolean;
  payment_instructions: string;
  // Automated reminder settings
  basic_reminders_enabled: boolean;
  reminder_channel: 'email' | 'sms' | 'email_then_sms' | 'both';
  invoice_reminder_before_due_enabled: boolean;
  invoice_reminder_before_due_days: number;
  invoice_reminder_on_due_enabled: boolean;
  invoice_reminder_after_due_enabled: boolean;
  invoice_reminder_after_due_days: number;
  invoice_reminder_max_count: number;
  quote_reminder_after_send_enabled: boolean;
  quote_reminder_after_send_days: number;
  quote_reminder_before_expiry_enabled: boolean;
  quote_reminder_before_expiry_days: number;
  quote_reminder_on_expiry_enabled: boolean;
  intelligent_reminders_enabled: boolean;
  // Notification preferences
  notify_email_payment_received: boolean;
  notify_push_payment_received: boolean;
  notify_email_quote_approved: boolean;
  notify_push_quote_approved: boolean;
  notify_email_quote_changes_requested: boolean;
  notify_push_quote_changes_requested: boolean;
  notify_email_ach_payment_failed: boolean;
  notify_push_ach_payment_failed: boolean;
  notify_email_bounced_delivery: boolean;
  notify_push_bounced_delivery: boolean;
  notify_email_refund_completed: boolean;
  notify_push_refund_completed: boolean;
  notify_email_refund_failed: boolean;
  notify_push_refund_failed: boolean;
  // Onboarding checklist
  onboarding?: {
    has_clients: boolean;
    has_documents: boolean;
    has_invoices: boolean;
    has_quotes: boolean;
    has_sent_document: boolean;
  };
}

export interface User {
  id: string;
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  is_email_verified: boolean;
  businesses: Business[];
  subscription?: { plan: string; status: string };
}

export interface AuthResponse {
  user: User;
  tokens: {
    access: string;
    refresh: string;
  };
}

export interface CheckEmailResponse {
  available: boolean;
  email: string;
}

export type IndustryChoice = [string, string];

export interface CurrencyChoice {
  value: string;
  label: string;
}

export interface CreateBusinessData {
  name: string;
  address: string;
  address_latitude?: number;
  address_longitude?: number;
  industry: string;
  industry_other?: string;
  default_currency?: string;
  logo?: string;
  // Automated reminder settings
  basic_reminders_enabled?: boolean;
  reminder_channel?: 'email' | 'sms' | 'email_then_sms' | 'both';
  invoice_reminder_before_due_enabled?: boolean;
  invoice_reminder_before_due_days?: number;
  invoice_reminder_on_due_enabled?: boolean;
  invoice_reminder_after_due_enabled?: boolean;
  invoice_reminder_after_due_days?: number;
  invoice_reminder_max_count?: number;
  quote_reminder_after_send_enabled?: boolean;
  quote_reminder_after_send_days?: number;
  quote_reminder_before_expiry_enabled?: boolean;
  quote_reminder_before_expiry_days?: number;
  quote_reminder_on_expiry_enabled?: boolean;
  intelligent_reminders_enabled?: boolean;
  // Notification preferences
  notify_email_payment_received?: boolean;
  notify_push_payment_received?: boolean;
  notify_email_quote_approved?: boolean;
  notify_push_quote_approved?: boolean;
  notify_email_quote_changes_requested?: boolean;
  notify_push_quote_changes_requested?: boolean;
  notify_email_ach_payment_failed?: boolean;
  notify_push_ach_payment_failed?: boolean;
  notify_email_bounced_delivery?: boolean;
  notify_push_bounced_delivery?: boolean;
  notify_email_refund_completed?: boolean;
  notify_push_refund_completed?: boolean;
  notify_email_refund_failed?: boolean;
  notify_push_refund_failed?: boolean;
}

export interface Phone {
  country_code: string;
  number: string;
}

export interface Client {
  id: string;
  billing_name: string;
  company_name: string;
  emails: string[];
  phones: Phone[];
  website: string;
  address: string;
  billing_address: string;
  tax_number: string;
  payment_terms: string;
  notes: string;
  primary_email: string;
  primary_phone: Phone;
  business: string;
  created_at: string;
  updated_at: string;
}

export interface ClientReview {
  id: string;
  business: string;
  client: string;
  client_name: string;
  invoice: string | null;
  invoice_number: string | null;
  rating: number;
  comments: string;
  created_at: string;
  updated_at: string;
}

export interface CreateClientData {
  billing_name: string;
  company_name?: string;
  emails?: string[];
  phones?: Phone[];
  website?: string;
  address?: string;
  billing_address?: string;
  tax_number?: string;
  payment_terms?: string;
  notes?: string;
  business: string;
}

export type PaymentTermsChoice = [string, string];

export interface Tax {
  id: string;
  name: string;
  rate: string;
  tax_type: string;
  apply_to_all_parts: boolean;
  apply_to_all_labor: boolean;
  is_archived: boolean;
  business: string;
  created_at: string;
  updated_at: string;
}

export interface CreateTaxData {
  name: string;
  rate: string;
  tax_type: string;
  apply_to_all_parts: boolean;
  apply_to_all_labor: boolean;
  business: string;
}

export type TaxTypeChoice = [string, string];

export interface Item {
  id: string;
  name: string;
  code: string;
  description: string;
  default_quantity: string;
  rate: string;  // Can be negative for discounts/coupons
  item_type: string;
  taxes: string[];
  quantity_label: string | null;
  quantity_label_text: string | null;
  business: string;
  created_at: string;
  updated_at: string;
}

export interface CreateItemData {
  name: string;
  code?: string;
  description?: string;
  default_quantity?: string;
  rate: string;
  item_type: string;
  taxes?: string[];
  quantity_label?: string | null;
  business: string;
}

export type ItemTypeChoice = [string, string];

// Item Import Types
export interface ItemImportPreviewRowData {
  name: string;
  code: string;
  description: string;
  rate: string;
  default_quantity: string;
  item_type: string;
  quantity_label: string;
}

export interface ItemImportPreviewRow {
  row_number: number;
  status: 'valid' | 'warning' | 'error' | 'duplicate';
  errors: string[];
  warnings: string[];
  data: ItemImportPreviewRowData;
}

export interface ItemImportPreviewSummary {
  total: number;
  valid: number;
  warnings: number;
  errors: number;
  duplicates: number;
}

export interface ItemImportPreviewResponse {
  summary: ItemImportPreviewSummary;
  rows: ItemImportPreviewRow[];
  column_mapping: Record<string, string>;
  detected_columns: string[];
}

export interface ItemImportExecuteResponse {
  created: number;
  updated: number;
  skipped: number;
  errors: { row: number; errors: string[] }[];
}

// Client Import Types
export interface ClientImportPreviewRowData {
  billing_name: string;
  company_name: string;
  email: string;
  phone: string;
  website: string;
  address: string;
  billing_address: string;
}

export interface ClientImportPreviewRow {
  row_number: number;
  status: 'valid' | 'warning' | 'error' | 'duplicate';
  errors: string[];
  warnings: string[];
  data: ClientImportPreviewRowData;
}

export interface ClientImportPreviewSummary {
  total: number;
  valid: number;
  warnings: number;
  errors: number;
  duplicates: number;
}

export interface ClientImportPreviewResponse {
  summary: ClientImportPreviewSummary;
  rows: ClientImportPreviewRow[];
  column_mapping: Record<string, string>;
  detected_columns: string[];
}

export interface ClientImportExecuteResponse {
  created: number;
  updated: number;
  skipped: number;
  errors: { row: number; errors: string[] }[];
}

export interface ItemQuantityLabel {
  id: string;
  label: string;
  item_type: string;
  is_default: boolean;
  business: string;
  created_at: string;
  updated_at: string;
}

export interface CreateItemQuantityLabelData {
  label: string;
  item_type: string;
  is_default: boolean;
  business: string;
}

export interface ItemBundleLink {
  id: string;
  item: string;
  item_name: string;
  item_rate: string;
  item_type: string;
  quantity: string;
  sort_order: number;
}

export interface ItemBundle {
  id: string;
  name: string;
  description: string;
  links: ItemBundleLink[];
  business: string;
  created_at: string;
  updated_at: string;
}

export interface CreateItemBundleLinkData {
  item: string;
  quantity: string;
  sort_order: number;
}

export interface CreateItemBundleData {
  name: string;
  description?: string;
  business: string;
  links_write: CreateItemBundleLinkData[];
}

export interface Contract {
  id: string;
  subject: string;
  body: string;
  is_default_estimate_contract: boolean;
  is_default_invoice_contract: boolean;
  business: string;
  created_at: string;
  updated_at: string;
}

export interface CreateContractData {
  subject: string;
  body: string;
  is_default_estimate_contract?: boolean;
  is_default_invoice_contract?: boolean;
  business: string;
}

// Contract Templates (new Tiptap-based templates)
export type ContractTemplateDocumentType = 'invoice' | 'quote' | 'credit_memo' | 'purchase_order' | 'standalone';

export interface ContractTemplate {
  id: string;
  business: string;
  document_type: ContractTemplateDocumentType;
  name: string;
  content: string;
  counterparty_signature_required: boolean;
  business_signature_required: boolean;
  variables_used: string[];
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateContractTemplateData {
  business: string;
  document_type: ContractTemplateDocumentType;
  name: string;
  content: string;
  counterparty_signature_required?: boolean;
  business_signature_required?: boolean;
  is_default?: boolean;
}

// Contract Variables (fetched from API)
export interface ContractVariable {
  id: string;
  label: string;
}

export interface ContractVariableCategory {
  id: string;
  name: string;
  document_types?: ContractTemplateDocumentType[];
  variables: ContractVariable[];
}

// Contract Instance (frozen snapshot of a contract template)
export interface ContractInstance {
  id: string;
  cached_content: string;
  cached_rendered_content: string;
  counterparty_signature_required: boolean;
  business_signature_required: boolean;
  variables_used: string[];
  // Business (sender) signature
  business_signatory: string;
  business_signature_image: string;
  business_signed_at: string | null;
  // Counterparty (client/vendor) signature
  counterparty_signatory: string;
  counterparty_signature_image: string;
  counterparty_signed_at: string | null;
  created_at: string;
  updated_at: string;
}

export type MarkupAppliedTo = 'none' | 'all' | 'labor' | 'product_parts' | 'other';

export interface LineItem {
  id?: string;  // temporary UUID for frontend tracking
  name: string;
  code: string;
  description: string;
  quantity: number;
  rate: number;
  tax_ids: string[];
  amount: number;
  quantity_label_text?: string | null;
  item_type?: string;  // 'product_parts' | 'labor'
  computed_rate?: number;  // rate after markup applied (for display)
  computed_amount?: number;  // amount after markup applied (for calculations)
}

export interface Attachment {
  url: string;
  filename: string;
  content_type: string;
  size: number;
  uploaded_at: string;
}

// Payment allocation display for invoice/quote view pages
export interface PaymentAllocationDisplay {
  id: string;
  payment_id: string;
  amount: string;
  payment_date: string;
  payment_method: string;
  payment_reference: string;
  is_credit_memo: boolean;
  is_stripe_payment: boolean;
  is_refund: boolean;
  is_refundable: boolean;
  refundable_amount: string;
  credit_memo_number?: string;
  status: 'completed' | 'pending' | 'failed';
  created_at: string;
}

export type DeliveryStatus = 'delivered' | 'opened' | 'clicked' | 'bounced' | null;

export interface InvoiceSendRecord {
  id: string;
  invoice: string;
  medium: 'email' | 'text' | 'marked_by_user';
  destination: string;
  message_copy: string;
  created_at: string;
  delivery_status: DeliveryStatus;
  delivery_status_updated_at: string | null;
}

export interface Invoice {
  id: string;
  invoice_number: string;
  business: string;
  client: string | null;
  client_name: string;
  client_company: string;
  client_address: string;
  client_billing_address: string;
  contract_template: string | null;
  contract_instance: ContractInstance | null;
  source_quote: string | null;
  invoice_date: string | null;  // ISO date string
  due_date: string | null;      // ISO date string
  payment_terms: string;
  status: InvoiceStatus;
  tax_mode?: 'line_items' | 'entire_document';
  line_items: [string, LineItem[]][]; // [section_name, items]
  deposit: string;
  stripe_card_enabled: boolean;
  stripe_ach_enabled: boolean;
  // Shipping
  ship_enabled: boolean;
  ship_fee: string | null;
  ship_date: string | null;
  ship_via: string;
  ship_tracking_number: string;
  requires_my_signature: boolean;  // Computed from contract template/instance
  requires_client_signature: boolean;  // Computed from contract template/instance
  business_signature_override: boolean | null;  // Override template's business signature requirement
  counterparty_signature_override: boolean | null;  // Override template's counterparty signature requirement
  note_for_client: string;
  internal_notes: string;
  job_location: string;
  attachments: Attachment[];
  markup_dollars: string;
  markup_percent: string;
  markup_applied_to: MarkupAppliedTo;
  discount_dollars: string;
  discount_percent: string;
  custom_field_values: Record<string, string>;
  subtotal: string;
  total_markup: string;
  total_discount: string;
  total_tax: string;
  total_amount: string;
  amount_paid: string;
  pending_payment_amount: string;
  balance_due: string;
  send_records?: InvoiceSendRecord[];
  payment_allocations?: PaymentAllocationDisplay[];
  pdf_url?: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateInvoiceData {
  business: string;
  client: string;
  invoice_date: string;
  due_date: string;
  payment_terms?: string;
  status?: string;
  tax_mode?: 'line_items' | 'entire_document';
  line_items?: [string, LineItem[]][];
  contract_template?: string | null;
  deposit?: number;
  stripe_card_enabled?: boolean;
  stripe_ach_enabled?: boolean;
  ship_enabled?: boolean;
  ship_fee?: number | null;
  ship_date?: string | null;
  ship_via?: string;
  ship_tracking_number?: string;
  note_for_client?: string;
  internal_notes?: string;
  job_location?: string;
  markup_dollars?: number;
  markup_percent?: number;
  markup_applied_to?: MarkupAppliedTo;
  discount_dollars?: number;
  discount_percent?: number;
  custom_field_values?: Record<string, string>;
  attachments?: Attachment[];
  business_signature_override?: boolean | null;
  counterparty_signature_override?: boolean | null;
}

export const authApi = {
  registration: (data: {
    email: string;
    password1: string;
    password2: string;
    first_name?: string;
    last_name?: string;
    website?: string;
  }) =>
    apiRequest<AuthResponse>('/auth/registration/', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  login: (email: string, password: string) =>
    apiRequest<AuthResponse>('/auth/login/', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  checkEmail: (email: string) =>
    apiRequest<CheckEmailResponse>('/auth/check-email/', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  passwordReset: (email: string) =>
    apiRequest<{ detail: string }>('/auth/password/reset/', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  passwordResetConfirm: (data: {
    uid: string;
    token: string;
    new_password1: string;
    new_password2: string;
  }) =>
    apiRequest<{ detail: string }>('/auth/password/reset/confirm/', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Social authentication
  googleLogin: (data: { access_token?: string; id_token?: string }) =>
    apiRequest<AuthResponse>('/auth/google/', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  appleLogin: (data: { access_token?: string; id_token: string }) =>
    apiRequest<AuthResponse>('/auth/apple/', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  facebookLogin: (accessToken: string) =>
    apiRequest<AuthResponse>('/auth/facebook/', {
      method: 'POST',
      body: JSON.stringify({ access_token: accessToken }),
    }),

  // Invite session (for social auth with pending invites)
  setInviteSession: (membershipId: string) =>
    apiRequest<{ status: string }>('/auth/set-invite/', {
      method: 'POST',
      body: JSON.stringify({ membership_id: membershipId }),
    }),

  getMe: () => apiRequest<User>('/auth/me/'),

  updateMe: (data: { first_name?: string; last_name?: string; email?: string }) =>
    apiRequest<User>('/auth/me/', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  impersonate: (email: string) =>
    apiRequest<AuthResponse>('/auth/impersonate/', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  requestEmailVerification: () =>
    apiRequest<{ detail: string }>('/auth/email-verification/request/', {
      method: 'POST',
    }),

  verifyEmail: (code: string) =>
    apiRequest<{ detail: string }>('/auth/email-verification/verify/', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),
};

export const businessApi = {
  list: () => apiRequest<Business[]>('/businesses/'),

  create: (data: CreateBusinessData) =>
    apiRequest<Business>('/businesses/', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  get: (id: string) => apiRequest<Business>(`/businesses/${id}/`),

  update: (id: string, data: Partial<CreateBusinessData>) =>
    apiRequest<Business>(`/businesses/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    apiRequest<void>(`/businesses/${id}/`, {
      method: 'DELETE',
    }),

  getIndustries: () => apiRequest<IndustryChoice[]>('/industries/'),

  getCurrencies: () => apiRequest<CurrencyChoice[]>('/currencies/'),

  getFeatureFlags: (id: string) => apiRequest<FeatureFlags>(`/businesses/${id}/feature-flags/`),
};

// =============================================================================
// TEMPLATE CONFIG
// =============================================================================

export interface TemplateConfig {
  id: string;
  template_name: string;
  settings: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export const templateConfigApi = {
  get: (businessId: string) =>
    apiRequest<TemplateConfig>(`/businesses/${businessId}/template-config/`),

  save: (businessId: string, data: { template_name: string; settings: Record<string, unknown> }) =>
    apiRequest<TemplateConfig>(`/businesses/${businessId}/template-config/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
};

// =============================================================================
// DOCUMENTS (Owl Ops Rendering)
// =============================================================================

export interface TemplateMetadata {
  slug: string;
  template_types: string[];
  preview_svg?: string;
  metadata: {
    name: string;
    description: string;
    category: string;
  };
}

export interface RenderDocumentRequest {
  template_slug?: string;
  template_content?: string;
  data: Record<string, unknown>;
  settings?: Record<string, unknown>;
}

export interface RenderPdfRequest extends RenderDocumentRequest {
  html_content?: string;
  pdf_options?: {
    format?: string;
    margin?: { top?: string; right?: string; bottom?: string; left?: string };
    headerHtml?: string;
    footerHtml?: string;
  };
}

export const documentsApi = {
  /** List available templates */
  listTemplates: (templateType?: 'document' | 'receipt') => {
    const params = templateType ? `?template_type=${templateType}` : '';
    return apiRequest<{ templates: TemplateMetadata[] }>(`/documents/templates/${params}`);
  },

  /** Get template bundle for client-side switching */
  getTemplateBundle: (options?: { documentType?: string; industry?: string }) => {
    const parts: string[] = [];
    if (options?.documentType) parts.push(`document_type=${options.documentType}`);
    if (options?.industry) parts.push(`industry=${options.industry}`);
    const params = parts.length ? `?${parts.join('&')}` : '';
    return apiRequest<{
      combined_html: string;
      templates: TemplateMetadata[];
      version: string;
    }>(`/documents/templates/bundle/${params}`);
  },

  /** Preview a template with sample data */
  previewTemplate: (templateSlug: string, documentType: string = 'invoice') =>
    apiRequest<{ html: string; sample_data: Record<string, unknown> }>(
      `/documents/templates/${templateSlug}/preview/`,
      {
        method: 'POST',
        body: JSON.stringify({ document_type: documentType }),
      }
    ),

  /** Render document to HTML */
  renderHtml: (request: RenderDocumentRequest) =>
    apiRequest<{ html: string }>('/documents/render/html/', {
      method: 'POST',
      body: JSON.stringify(request),
    }),

  /** Render document to PDF (returns blob) */
  renderPdf: async (request: RenderPdfRequest): Promise<Blob> => {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_BASE_URL}/documents/render/pdf/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'PDF rendering failed');
    }

    return response.blob();
  },

  /**
   * Render document by ID to HTML (single source of truth - backend builds payload).
   * Use this instead of renderHtml for real documents.
   */
  renderByIdHtml: (request: {
    document_id: string;
    document_type: 'invoice' | 'quote' | 'credit_memo' | 'purchase_order' | 'receipt';
    settings?: Record<string, unknown>;
  }) =>
    apiRequest<{ html: string }>('/documents/render-by-id/html/', {
      method: 'POST',
      body: JSON.stringify(request),
    }),
};

// =============================================================================
// FEATURE FLAGS
// =============================================================================

export interface FeatureFlags {
  // Stripe Connect - Payment Processing
  can_accept_payments: boolean;
  payments_setup_complete: boolean;
  stripe_account_id: string | null;

  // QuickBooks - Accounting
  can_sync_quickbooks: boolean;
  quickbooks_connected: boolean;

  // Subscription tier (from backend PLAN_CHOICES)
  plan: string;
  can_use_recurring: boolean;
  can_use_custom_branding: boolean;
  can_export_reports: boolean;
  can_use_team_members: boolean;
  can_use_sms_reminders: boolean;
  can_use_intelligent_reminders: boolean;

  // Upsell indicators
  show_stripe_upsell: boolean;
  show_qb_upsell: boolean;
  show_upgrade_upsell: boolean;
}

export const clientApi = {
  list: (businessId?: string, search?: string) => {
    const params = new URLSearchParams();
    if (businessId) params.append('business_id', businessId);
    if (search) params.append('search', search);
    const queryString = params.toString() ? `?${params.toString()}` : '';
    return apiRequest<Client[]>(`/clients/${queryString}`);
  },

  create: (data: CreateClientData) =>
    apiRequest<Client>('/clients/', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  get: (id: string) => apiRequest<Client>(`/clients/${id}/`),

  update: (id: string, data: Partial<CreateClientData>) =>
    apiRequest<Client>(`/clients/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    apiRequest<void>(`/clients/${id}/`, {
      method: 'DELETE',
    }),

  getPaymentTerms: () => apiRequest<PaymentTermsChoice[]>('/payment-terms/'),

  // Import methods
  importPreview: async (businessId: string, file: File, isRetry = false): Promise<ApiResponse<ClientImportPreviewResponse>> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('business_id', businessId);

    const token = await getValidToken();
    const response = await fetch(`${API_BASE_URL}/clients/import/preview/`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json();

      // Check for invalid/expired token
      if (errorData.code === 'token_not_valid' && !isRetry) {
        if (!isRefreshing) {
          isRefreshing = true;
          refreshPromise = refreshAccessToken();
        }

        const newToken = await refreshPromise;
        isRefreshing = false;
        refreshPromise = null;

        if (newToken) {
          return clientApi.importPreview(businessId, file, true);
        }

        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return { error: 'Session expired. Please login again.' };
      }

      return { error: errorData.error || errorData.detail || 'Upload failed' };
    }

    const data = await response.json();
    return { data };
  },

  importExecute: async (businessId: string, file: File, duplicateHandling: 'skip' | 'update' | 'create' = 'skip', isRetry = false): Promise<ApiResponse<ClientImportExecuteResponse>> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('business_id', businessId);
    formData.append('duplicate_handling', duplicateHandling);

    const token = await getValidToken();
    const response = await fetch(`${API_BASE_URL}/clients/import/execute/`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json();

      // Check for invalid/expired token
      if (errorData.code === 'token_not_valid' && !isRetry) {
        if (!isRefreshing) {
          isRefreshing = true;
          refreshPromise = refreshAccessToken();
        }

        const newToken = await refreshPromise;
        isRefreshing = false;
        refreshPromise = null;

        if (newToken) {
          return clientApi.importExecute(businessId, file, duplicateHandling, true);
        }

        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return { error: 'Session expired. Please login again.' };
      }

      return { error: errorData.error || errorData.detail || 'Import failed' };
    }

    const data = await response.json();
    return { data };
  },

  getImportTemplateUrl: (businessId: string) => {
    return `${API_BASE_URL}/clients/import/template/?business_id=${businessId}`;
  },

  downloadImportTemplate: async (businessId: string): Promise<void> => {
    const token = localStorage.getItem('accessToken');
    const url = `${API_BASE_URL}/clients/import/template/?business_id=${businessId}`;

    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      throw new Error('Failed to download template');
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = 'client_import_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(downloadUrl);
  },
};

export const clientReviewApi = {
  list: (businessId: string) => {
    const params = new URLSearchParams({ business_id: businessId });
    return apiRequest<ClientReview[]>(`/client-reviews/?${params.toString()}`);
  },
};

export const taxApi = {
  list: (businessId?: string, showArchived?: boolean) => {
    const params = new URLSearchParams();
    if (businessId) params.append('business_id', businessId);
    if (showArchived) params.append('show_archived', 'true');
    const queryString = params.toString() ? `?${params.toString()}` : '';
    return apiRequest<Tax[]>(`/taxes/${queryString}`);
  },

  create: (data: CreateTaxData) =>
    apiRequest<Tax>('/taxes/', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  get: (id: string) => apiRequest<Tax>(`/taxes/${id}/`),

  update: (id: string, data: Partial<CreateTaxData & { is_archived: boolean }>) =>
    apiRequest<Tax>(`/taxes/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    apiRequest<void>(`/taxes/${id}/`, {
      method: 'DELETE',
    }),

  archive: (id: string) =>
    apiRequest<Tax>(`/taxes/${id}/`, {
      method: 'PUT',
      body: JSON.stringify({ is_archived: true }),
    }),

  unarchive: (id: string) =>
    apiRequest<Tax>(`/taxes/${id}/`, {
      method: 'PUT',
      body: JSON.stringify({ is_archived: false }),
    }),

  getTaxTypes: () => apiRequest<TaxTypeChoice[]>('/tax-types/'),
};

export const itemApi = {
  list: (businessId?: string, pagination?: PaginationParams) => {
    const params = new URLSearchParams();
    if (businessId) params.append('business_id', businessId);
    if (pagination?.limit) params.append('limit', pagination.limit.toString());
    if (pagination?.offset !== undefined) params.append('offset', pagination.offset.toString());
    if (pagination?.ordering) params.append('ordering', pagination.ordering);
    const queryString = params.toString() ? `?${params.toString()}` : '';
    return apiRequest<PaginatedResponse<Item>>(`/items/${queryString}`);
  },

  create: (data: CreateItemData) =>
    apiRequest<Item>('/items/', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  get: (id: string) => apiRequest<Item>(`/items/${id}/`),

  update: (id: string, data: Partial<CreateItemData>) =>
    apiRequest<Item>(`/items/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    apiRequest<void>(`/items/${id}/`, {
      method: 'DELETE',
    }),

  getItemTypes: () => apiRequest<ItemTypeChoice[]>('/item-types/'),

  // Import methods
  importPreview: async (businessId: string, file: File, isRetry = false): Promise<ApiResponse<ItemImportPreviewResponse>> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('business_id', businessId);

    const token = await getValidToken();
    const response = await fetch(`${API_BASE_URL}/items/import/preview/`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json();

      // Check for invalid/expired token
      if (errorData.code === 'token_not_valid' && !isRetry) {
        if (!isRefreshing) {
          isRefreshing = true;
          refreshPromise = refreshAccessToken();
        }

        const newToken = await refreshPromise;
        isRefreshing = false;
        refreshPromise = null;

        if (newToken) {
          return itemApi.importPreview(businessId, file, true);
        }

        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return { error: 'Session expired. Please login again.' };
      }

      return { error: errorData.error || errorData.detail || 'Upload failed' };
    }

    const data = await response.json();
    return { data };
  },

  importExecute: async (businessId: string, file: File, duplicateHandling: 'skip' | 'update' | 'create' = 'skip', isRetry = false): Promise<ApiResponse<ItemImportExecuteResponse>> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('business_id', businessId);
    formData.append('duplicate_handling', duplicateHandling);

    const token = await getValidToken();
    const response = await fetch(`${API_BASE_URL}/items/import/execute/`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json();

      // Check for invalid/expired token
      if (errorData.code === 'token_not_valid' && !isRetry) {
        if (!isRefreshing) {
          isRefreshing = true;
          refreshPromise = refreshAccessToken();
        }

        const newToken = await refreshPromise;
        isRefreshing = false;
        refreshPromise = null;

        if (newToken) {
          return itemApi.importExecute(businessId, file, duplicateHandling, true);
        }

        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return { error: 'Session expired. Please login again.' };
      }

      return { error: errorData.error || errorData.detail || 'Import failed' };
    }

    const data = await response.json();
    return { data };
  },

  getImportTemplateUrl: (businessId: string) => {
    return `${API_BASE_URL}/items/import/template/?business_id=${businessId}`;
  },

  downloadImportTemplate: async (businessId: string): Promise<void> => {
    const token = localStorage.getItem('accessToken');
    const url = `${API_BASE_URL}/items/import/template/?business_id=${businessId}`;

    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      throw new Error('Failed to download template');
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = 'item_import_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(downloadUrl);
  },
};

export const itemQuantityLabelApi = {
  list: (businessId: string, itemType?: string) => {
    const params = new URLSearchParams();
    params.append('business_id', businessId);
    if (itemType) params.append('item_type', itemType);
    return apiRequest<ItemQuantityLabel[]>(`/item-quantity-labels/?${params.toString()}`);
  },

  create: (data: CreateItemQuantityLabelData) =>
    apiRequest<ItemQuantityLabel>('/item-quantity-labels/', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

export const itemBundleApi = {
  list: (businessId: string) => {
    const params = new URLSearchParams();
    params.append('business_id', businessId);
    return apiRequest<ItemBundle[]>(`/item-bundles/?${params.toString()}`);
  },

  create: (data: CreateItemBundleData) =>
    apiRequest<ItemBundle>('/item-bundles/', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  get: (id: string) => apiRequest<ItemBundle>(`/item-bundles/${id}/`),

  update: (id: string, data: CreateItemBundleData) =>
    apiRequest<ItemBundle>(`/item-bundles/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    apiRequest<void>(`/item-bundles/${id}/`, {
      method: 'DELETE',
    }),
};

/**
 * @deprecated Legacy Contract API - Backend endpoints removed.
 * This API is retained temporarily for EditQuote.tsx compatibility.
 * Will be removed when quotes are refactored to use ContractTemplate.
 * DO NOT USE for new code.
 */
/* eslint-disable @typescript-eslint/no-unused-vars */
export const contractApi = {
  list: (_businessId?: string): Promise<ApiResponse<Contract[]>> => {
    // Backend endpoint removed - return empty list
    return Promise.resolve({ data: [], error: undefined });
  },

  create: (_data: CreateContractData): Promise<ApiResponse<Contract>> => {
    return Promise.resolve({ data: null as unknown as Contract, error: 'Legacy Contract API removed' });
  },

  get: (_id: string): Promise<ApiResponse<Contract>> => {
    return Promise.resolve({ data: null as unknown as Contract, error: 'Legacy Contract API removed' });
  },

  update: (_id: string, _data: Partial<CreateContractData>): Promise<ApiResponse<Contract>> => {
    return Promise.resolve({ data: null as unknown as Contract, error: 'Legacy Contract API removed' });
  },

  delete: (_id: string): Promise<ApiResponse<void>> => {
    return Promise.resolve({ data: undefined, error: 'Legacy Contract API removed' });
  },
};
/* eslint-enable @typescript-eslint/no-unused-vars */

export const contractTemplateApi = {
  list: (businessId?: string, documentType?: string) => {
    const params = new URLSearchParams();
    if (businessId) params.append('business_id', businessId);
    if (documentType) params.append('document_type', documentType);
    const queryString = params.toString() ? `?${params.toString()}` : '';
    return apiRequest<ContractTemplate[]>(`/contract-templates/${queryString}`);
  },

  create: (data: CreateContractTemplateData) =>
    apiRequest<ContractTemplate>('/contract-templates/', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  get: (id: string) => apiRequest<ContractTemplate>(`/contract-templates/${id}/`),

  update: (id: string, data: Partial<CreateContractTemplateData>) =>
    apiRequest<ContractTemplate>(`/contract-templates/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    apiRequest<void>(`/contract-templates/${id}/`, {
      method: 'DELETE',
    }),

  setDefault: (id: string) =>
    apiRequest<ContractTemplate>(`/contract-templates/${id}/set-default/`, {
      method: 'POST',
    }),

  getVariables: (documentType?: ContractTemplateDocumentType) => {
    const params = documentType ? `?document_type=${documentType}` : '';
    return apiRequest<ContractVariableCategory[]>(`/contract-variables/${params}`);
  },
};

export const invoiceApi = {
  list: async (
    businessId?: string,
    clientIds?: string[],
    status?: string,
    startDate?: string,
    endDate?: string,
    pagination?: PaginationParams,
    search?: string
  ) => {
    const params = new URLSearchParams();
    if (businessId) params.append('business_id', businessId);
    if (clientIds && clientIds.length > 0) params.append('client_ids', clientIds.join(','));
    if (status) params.append('status', status);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (pagination?.limit) params.append('limit', pagination.limit.toString());
    if (pagination?.offset !== undefined) params.append('offset', pagination.offset.toString());
    if (pagination?.ordering) params.append('ordering', pagination.ordering);
    if (search) params.append('search', search);

    return apiRequest<PaginatedResponse<Invoice>>(`/invoices/?${params.toString()}`, {
      method: 'GET'
    });
  },

  create: async (data: CreateInvoiceData) => {
    return apiRequest<Invoice>('/invoices/', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  get: async (id: string) => {
    return apiRequest<Invoice>(`/invoices/${id}/`, {
      method: 'GET'
    });
  },

  update: async (id: string, data: Partial<CreateInvoiceData>) => {
    return apiRequest<Invoice>(`/invoices/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  updateNumber: async (id: string, invoice_number: string) => {
    return apiRequest<Invoice>(`/invoices/${id}/number/`, {
      method: 'PATCH',
      body: JSON.stringify({ invoice_number })
    });
  },

  delete: async (id: string) => {
    return apiRequest<void>(`/invoices/${id}/`, {
      method: 'DELETE'
    });
  },

  uploadAttachment: async (invoiceId: string, file: File, isRetry = false): Promise<ApiResponse<unknown>> => {
    const formData = new FormData();
    formData.append('file', file);

    const token = await getValidToken();
    const response = await fetch(`${API_BASE_URL}/invoices/${invoiceId}/attachments/`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json();

      // Check for invalid/expired token
      if (errorData.code === 'token_not_valid' && !isRetry) {
        // Try to refresh token
        if (!isRefreshing) {
          isRefreshing = true;
          refreshPromise = refreshAccessToken();
        }

        const newToken = await refreshPromise;
        isRefreshing = false;
        refreshPromise = null;

        if (newToken) {
          // Retry the upload with new token
          return invoiceApi.uploadAttachment(invoiceId, file, true);
        }

        // Refresh failed, clear tokens and redirect
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return { error: 'Session expired. Please login again.' };
      }

      return { error: errorData.detail || 'Upload failed' };
    }

    const data = await response.json();
    return { data };
  },

  deleteAttachment: async (invoiceId: string, attachmentIndex: number) => {
    return apiRequest<{ detail: string }>(`/invoices/${invoiceId}/attachments/${attachmentIndex}/`, {
      method: 'DELETE'
    });
  },

  send: async (invoiceId: string, data: {
    medium: 'email' | 'text' | 'marked_by_user';
    destination: string;
    subject?: string;
    message?: string;
    cc_self?: boolean;
  }) => {
    return apiRequest<{
      invoice: Invoice;
      send_records: InvoiceSendRecord[];
      // Note: send_record is created asynchronously by background task for email/text
    }>(`/invoices/${invoiceId}/send/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  remind: async (invoiceId: string, data: {
    medium: 'email' | 'text';
    destination: string;
    subject?: string;
    message?: string;
  }) => {
    return apiRequest<Invoice>(`/invoices/${invoiceId}/remind/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  signBusiness: async (invoiceId: string, signatory_name: string, signature_image: string) => {
    return apiRequest<Invoice>(`/invoices/${invoiceId}/sign-business/`, {
      method: 'POST',
      body: JSON.stringify({ signatory_name, signature_image }),
    });
  },

  revoke: async (id: string) => {
    return apiRequest<Invoice>(`/invoices/${id}/revoke/`, {
      method: 'POST',
    });
  },

  archive: async (id: string) => {
    return apiRequest<Invoice>(`/invoices/${id}/archive/`, {
      method: 'POST',
    });
  },

  getSendTemplate: async (invoiceId: string, medium: 'email' | 'sms' = 'email', purpose?: 'reminder') => {
    const params = new URLSearchParams({ medium });
    if (purpose === 'reminder') {
      params.set('purpose', 'reminder_invoice');
    }
    return apiRequest<{ subject: string; body: string; is_custom: boolean }>(
      `/invoices/${invoiceId}/send-template/?${params}`
    );
  },

  markBadDebt: async (id: string) => {
    return apiRequest<Invoice>(`/invoices/${id}/mark-bad-debt/`, {
      method: 'POST',
    });
  },

  generatePdf: async (id: string) => {
    return apiRequest<{ pdf_url: string }>(`/invoices/${id}/generate-pdf/`, {
      method: 'POST',
    });
  },
};

// =============================================================================
// QUOTE API
// =============================================================================

export type QuoteStatus = 'draft' | 'awaiting_response' | 'approved' | 'changes_requested' | 'converted';

export interface QuoteSendRecord {
  id: string;
  quote: string;
  medium: 'email' | 'text' | 'marked_by_user';
  destination: string;
  message_copy: string;
  created_at: string;
  delivery_status: DeliveryStatus;
  delivery_status_updated_at: string | null;
}

export interface QuoteChangeRequest {
  id: string;
  text: string;
  created_at: string;
  updated_at: string;
}

export interface Quote {
  id: string;
  quote_number: string;
  business: string;
  client: string | null;
  client_name: string;
  client_company: string;
  client_address: string;
  client_billing_address: string;
  client_public_id: string | null;
  contract_template: string | null;
  contract_instance: ContractInstance | null;
  converted_invoice: { id: string; invoice_number: string } | null;
  quote_date: string | null;
  due_date: string | null;
  status: QuoteStatus;
  tax_mode?: 'line_items' | 'entire_document';
  deposit: string;
  stripe_card_enabled: boolean;
  stripe_ach_enabled: boolean;
  // Shipping
  ship_enabled: boolean;
  ship_fee: string | null;
  ship_date: string | null;
  ship_via: string;
  ship_tracking_number: string;
  line_items: [string, LineItem[]][];
  requires_my_signature: boolean;
  requires_client_signature: boolean;
  business_signature_override: boolean | null;
  counterparty_signature_override: boolean | null;
  note_for_client: string;
  internal_notes: string;
  job_location: string;
  attachments: Attachment[];
  markup_dollars: string;
  markup_percent: string;
  markup_applied_to: MarkupAppliedTo;
  discount_dollars: string;
  discount_percent: string;
  custom_field_values: Record<string, string>;
  subtotal: string;
  total_markup: string;
  total_discount: string;
  total_tax: string;
  total_amount: string;
  amount_paid: string;
  pending_payment_amount: string;
  balance_due: string;
  deposit_fully_paid: boolean;
  send_records?: QuoteSendRecord[];
  payment_allocations?: PaymentAllocationDisplay[];
  change_requests?: QuoteChangeRequest[];
  pdf_url?: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateQuoteData {
  business: string;
  client: string;
  quote_date?: string | null;
  due_date?: string | null;
  status?: string;
  tax_mode?: 'line_items' | 'entire_document';
  deposit?: number;
  stripe_card_enabled?: boolean;
  stripe_ach_enabled?: boolean;
  ship_enabled?: boolean;
  ship_fee?: number | null;
  ship_date?: string | null;
  ship_via?: string;
  ship_tracking_number?: string;
  line_items?: [string, LineItem[]][];
  contract_template?: string | null;
  note_for_client?: string;
  internal_notes?: string;
  job_location?: string;
  markup_dollars?: number;
  markup_percent?: number;
  markup_applied_to?: MarkupAppliedTo;
  discount_dollars?: number;
  discount_percent?: number;
  custom_field_values?: Record<string, string>;
  business_signature_override?: boolean | null;
  counterparty_signature_override?: boolean | null;
}

export const quoteApi = {
  list: async (
    businessId?: string,
    clientIds?: string[],
    status?: string,
    startDate?: string,
    endDate?: string,
    pagination?: PaginationParams,
    search?: string
  ) => {
    const params = new URLSearchParams();
    if (businessId) params.append('business_id', businessId);
    if (clientIds && clientIds.length > 0) params.append('client_ids', clientIds.join(','));
    if (status) params.append('status', status);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (pagination?.limit) params.append('limit', pagination.limit.toString());
    if (pagination?.offset !== undefined) params.append('offset', pagination.offset.toString());
    if (pagination?.ordering) params.append('ordering', pagination.ordering);
    if (search) params.append('search', search);
    return apiRequest<PaginatedResponse<Quote>>(`/quotes/?${params.toString()}`, { method: 'GET' });
  },

  create: async (data: CreateQuoteData) => {
    return apiRequest<Quote>('/quotes/', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  get: async (id: string) => {
    return apiRequest<Quote>(`/quotes/${id}/`, { method: 'GET' });
  },

  update: async (id: string, data: Partial<CreateQuoteData>) => {
    return apiRequest<Quote>(`/quotes/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  updateNumber: async (id: string, quote_number: string) => {
    return apiRequest<Quote>(`/quotes/${id}/number/`, {
      method: 'PATCH',
      body: JSON.stringify({ quote_number })
    });
  },

  delete: async (id: string) => {
    return apiRequest<void>(`/quotes/${id}/`, { method: 'DELETE' });
  },

  uploadAttachment: async (quoteId: string, file: File, isRetry = false): Promise<ApiResponse<unknown>> => {
    const formData = new FormData();
    formData.append('file', file);

    const token = await getValidToken();
    const response = await fetch(`${API_BASE_URL}/quotes/${quoteId}/attachments/`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json();
      if (errorData.code === 'token_not_valid' && !isRetry) {
        if (!isRefreshing) {
          isRefreshing = true;
          refreshPromise = refreshAccessToken();
        }
        const newToken = await refreshPromise;
        isRefreshing = false;
        refreshPromise = null;
        if (newToken) {
          return quoteApi.uploadAttachment(quoteId, file, true);
        }
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return { error: 'Session expired. Please login again.' };
      }
      return { error: errorData.detail || 'Upload failed' };
    }

    const data = await response.json();
    return { data };
  },

  deleteAttachment: async (quoteId: string, attachmentIndex: number) => {
    return apiRequest<{ detail: string }>(`/quotes/${quoteId}/attachments/${attachmentIndex}/`, {
      method: 'DELETE'
    });
  },

  send: async (quoteId: string, data: {
    medium: 'email' | 'text' | 'marked_by_user';
    destination: string;
    subject?: string;
    message?: string;
    cc_self?: boolean;
  }) => {
    return apiRequest<{
      quote: Quote;
      send_records: QuoteSendRecord[];
      // Note: send_record is created asynchronously by background task for email/text
    }>(`/quotes/${quoteId}/send/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  remind: async (quoteId: string, data: {
    medium: 'email' | 'text';
    destination: string;
    subject?: string;
    message?: string;
  }) => {
    return apiRequest<Quote>(`/quotes/${quoteId}/remind/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  convertToInvoice: async (quoteId: string, dueDate: string) => {
    return apiRequest<{
      invoice: Invoice;
      quote: Quote;
    }>(`/quotes/${quoteId}/convert/`, {
      method: 'POST',
      body: JSON.stringify({ due_date: dueDate }),
    });
  },

  approve: async (quoteId: string) => {
    return apiRequest<Quote>(`/quotes/${quoteId}/approve/`, {
      method: 'POST',
    });
  },

  archive: async (quoteId: string) => {
    return apiRequest<Quote>(`/quotes/${quoteId}/archive/`, {
      method: 'POST',
    });
  },

  revoke: async (id: string) => {
    return apiRequest<Quote>(`/quotes/${id}/revoke/`, {
      method: 'POST',
    });
  },

  signBusiness: async (quoteId: string, signatory_name: string, signature_image: string) => {
    return apiRequest<Quote>(`/quotes/${quoteId}/sign-business/`, {
      method: 'POST',
      body: JSON.stringify({ signatory_name, signature_image }),
    });
  },

  getSendTemplate: async (quoteId: string, medium: 'email' | 'sms' = 'email', purpose?: 'reminder') => {
    const params = new URLSearchParams({ medium });
    if (purpose === 'reminder') {
      params.set('purpose', 'reminder');
    }
    return apiRequest<{ subject: string; body: string; is_custom: boolean }>(
      `/quotes/${quoteId}/send-template/?${params}`
    );
  },

  generatePdf: async (id: string) => {
    return apiRequest<{ pdf_url: string }>(`/quotes/${id}/generate-pdf/`, {
      method: 'POST',
    });
  },
};



// =============================================================================
// CREDIT MEMO API
// =============================================================================

export type CreditMemoStatus = 'draft' | 'awaiting_allocation' | 'allocated' | 'cancelled';

// Allocation display for credit memo view (shows where credit was applied)
export interface CreditMemoAllocationDisplay {
  id: string;
  invoice?: string;
  invoice_number?: string;
  quote?: string;
  quote_number?: string;
  amount: string;
  created_at: string;
}

// Delete preview response
export interface CreditMemoDeletePreview {
  has_allocations: boolean;
  total_allocated: string;
  affected_invoices: {
    id: string;
    invoice_number: string;
    current_status: string;
    will_revert_to: string;
    allocated_amount: string;
  }[];
  affected_quotes: {
    id: string;
    quote_number: string;
    current_status: string;
    will_revert_to: string;
    allocated_amount: string;
  }[];
}

export interface CreditMemoSendRecord {
  id: string;
  credit_memo: string;
  medium: 'email' | 'text' | 'marked_by_user';
  destination: string;
  message_copy: string;
  created_at: string;
  delivery_status: DeliveryStatus;
  delivery_status_updated_at: string | null;
}

export interface CreditMemo {
  id: string;
  business: string;
  client: string | null;
  client_name?: string;
  client_company?: string;
  client_address?: string;
  credit_memo_number: string;
  credit_memo_date: string | null;
  status: CreditMemoStatus;
  tax_mode?: 'line_items' | 'entire_document';
  line_items: [string, LineItem[]][];
  note_for_client: string;
  internal_notes: string;
  attachments: Attachment[];
  markup_dollars: string;
  markup_percent: string;
  markup_applied_to: MarkupAppliedTo;
  custom_field_values: Record<string, string>;
  subtotal: string;
  total_markup: string;
  total_tax: string;
  total_amount: string;
  amount_allocated: string;
  balance_remaining: string;
  fully_allocated: boolean;
  send_records?: CreditMemoSendRecord[];
  allocations?: CreditMemoAllocationDisplay[];
  pdf_url?: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateCreditMemoData {
  business: string;
  client?: string | null;
  credit_memo_date?: string | null;
  status?: string;
  tax_mode?: 'line_items' | 'entire_document';
  line_items?: [string, LineItem[]][];
  note_for_client?: string;
  internal_notes?: string;
  markup_dollars?: number;
  markup_percent?: number;
  markup_applied_to?: MarkupAppliedTo;
  custom_field_values?: Record<string, string>;
}

// Allocation info for applying credit memo
export interface CreditMemoAllocationInfo {
  credit_memo: CreditMemo;
  outstanding_invoices: OutstandingInvoice[];
  outstanding_quotes: OutstandingQuote[];
  total_outstanding: string;
}

export interface ApplyCreditMemoAllocation {
  invoice?: string;
  quote?: string;
  amount: string;
}

export interface ApplyCreditMemoRequest {
  allocations: ApplyCreditMemoAllocation[];
}

export interface ApplyCreditMemoResponse {
  credit_memo: CreditMemo;
  allocations_created: number;
  amount_applied: string;
}

export const creditMemoApi = {
  list: async (
    businessId?: string,
    clientId?: string,
    status?: string,
    startDate?: string,
    endDate?: string,
    pagination?: PaginationParams
  ) => {
    const params = new URLSearchParams();
    if (businessId) params.append('business_id', businessId);
    if (clientId) params.append('client_id', clientId);
    if (status) params.append('status', status);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (pagination?.limit) params.append('limit', pagination.limit.toString());
    if (pagination?.offset !== undefined) params.append('offset', pagination.offset.toString());
    if (pagination?.ordering) params.append('ordering', pagination.ordering);
    return apiRequest<PaginatedResponse<CreditMemo>>(`/credit-memos/?${params.toString()}`, { method: 'GET' });
  },

  create: async (data: CreateCreditMemoData) => {
    return apiRequest<CreditMemo>('/credit-memos/', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  get: async (id: string) => {
    return apiRequest<CreditMemo>(`/credit-memos/${id}/`, { method: 'GET' });
  },

  update: async (id: string, data: Partial<CreateCreditMemoData>) => {
    return apiRequest<CreditMemo>(`/credit-memos/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  updateNumber: async (id: string, credit_memo_number: string) => {
    return apiRequest<CreditMemo>(`/credit-memos/${id}/number/`, {
      method: 'PATCH',
      body: JSON.stringify({ credit_memo_number })
    });
  },

  delete: async (id: string) => {
    return apiRequest<void>(`/credit-memos/${id}/`, { method: 'DELETE' });
  },

  getDeletePreview: async (id: string) => {
    return apiRequest<CreditMemoDeletePreview>(`/credit-memos/${id}/delete-preview/`, { method: 'GET' });
  },

  send: async (id: string, data: {
    medium: 'email' | 'text' | 'marked_by_user';
    destination: string;
    message: string;
    cc_self: boolean;
  }) => {
    return apiRequest<{
      credit_memo: CreditMemo;
      send_records: CreditMemoSendRecord[];
      // Note: send_record is created asynchronously by background task for email/text
    }>(`/credit-memos/${id}/send/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getAllocationInfo: async (id: string) => {
    return apiRequest<CreditMemoAllocationInfo>(`/credit-memos/${id}/allocation-info/`, {
      method: 'GET',
    });
  },

  applyCredit: async (id: string, data: ApplyCreditMemoRequest) => {
    return apiRequest<ApplyCreditMemoResponse>(`/credit-memos/${id}/apply/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  archive: async (id: string) => {
    return apiRequest<CreditMemo>(`/credit-memos/${id}/archive/`, {
      method: 'POST',
    });
  },

  getSendTemplate: async (creditMemoId: string, medium: 'email' | 'sms' = 'email') => {
    return apiRequest<{ subject: string; body: string; is_custom: boolean }>(
      `/credit-memos/${creditMemoId}/send-template/?medium=${medium}`
    );
  },
};

// =============================================================================
// DASHBOARD API
// =============================================================================

export interface DashboardStats {
  open_quotes: number;
  requires_invoicing: number;
  open_invoices: number;
  closed_invoices: number;
}

export interface InvoiceCounts {
  total: number;
  draft: number;
  awaiting_payment: number;
  overdue: number;
  paid: number;
  bad_debt: number;
  total_outstanding: string;
  awaiting_payment_amount: string;
  paid_this_month: string;
}

export interface QuoteCounts {
  total: number;
  draft: number;
  awaiting_response: number;
  approved: number;
  changes_requested: number;
  converted: number;
  archived: number;
  approved_this_month: number;
}

export interface CreditMemoCounts {
  total: number;
  draft: number;
  awaiting_allocation: number;
  allocated: number;
  cancelled: number;
}

export interface DashboardChartDataPoint {
  date: string;
  amount: number;
}

export interface DashboardKeyStats {
  avg_days_to_paid: number | null;
  avg_invoice_size: number;
  clients_invoiced: number;
}

export interface DashboardTopClient {
  id: string;
  name: string;
  total_revenue: number;
}

export interface DashboardActionCards {
  overdue_invoices: { count: number; amount: number };
  accepted_quotes: { count: number; amount: number };
  changes_requested_quotes: { count: number; amount: number };
}

export interface DashboardChartData {
  cumulative_revenue: DashboardChartDataPoint[];
  cumulative_payments: DashboardChartDataPoint[];
  key_stats: DashboardKeyStats;
  top_clients: DashboardTopClient[];
  action_cards: DashboardActionCards;
}

export const dashboardApi = {
  getStats: (businessId: string) => {
    const params = new URLSearchParams();
    params.append('business_id', businessId);
    return apiRequest<DashboardStats>(`/dashboard/stats/?${params.toString()}`);
  },

  getChartData: (businessId: string, days?: number) => {
    const params = new URLSearchParams();
    params.append('business_id', businessId);
    if (days) params.append('days', days.toString());
    return apiRequest<DashboardChartData>(`/dashboard/charts/?${params.toString()}`);
  },

  getInvoiceCounts: (businessId: string, clientIds?: string[]) => {
    const params = new URLSearchParams();
    params.append('business_id', businessId);
    if (clientIds && clientIds.length > 0) params.append('client_ids', clientIds.join(','));
    return apiRequest<InvoiceCounts>(`/invoices/counts/?${params.toString()}`);
  },

  getQuoteCounts: (businessId: string, clientIds?: string[]) => {
    const params = new URLSearchParams();
    params.append('business_id', businessId);
    if (clientIds && clientIds.length > 0) params.append('client_ids', clientIds.join(','));
    return apiRequest<QuoteCounts>(`/quotes/counts/?${params.toString()}`);
  },

  getCreditMemoCounts: (businessId: string) => {
    const params = new URLSearchParams();
    params.append('business_id', businessId);
    return apiRequest<CreditMemoCounts>(`/credit-memos/counts/?${params.toString()}`);
  },
};
// =============================================================================
// PUBLIC CLIENT PORTAL API (Unauthenticated)
// =============================================================================

export interface PublicClientInfo {
  id: string;
  billing_name: string;
  company_name?: string;
}

export interface PublicBusinessInfo {
  name: string;
  logo: string;
  address?: string;
  default_currency?: string;
}

export interface PublicPortalResponse {
  client: PublicClientInfo;
  business: PublicBusinessInfo;
}

export interface PublicInvoiceListItem {
  id: string;
  invoice_number: string;
  invoice_date: string | null;
  due_date: string | null;
  status: string;
  total_amount: string;
}

export interface PublicInvoicesResponse {
  invoices: PublicInvoiceListItem[];
  client: {
    id: string;
    billing_name: string;
  };
}

export interface PublicContractInstance {
  id: string;
  cached_rendered_content: string;
  counterparty_signature_required: boolean;
  business_signature_required: boolean;
  counterparty_signatory: string;
  counterparty_signature_image: string;
  counterparty_signed_at: string | null;
  business_signatory: string;
  business_signature_image: string;
  business_signed_at: string | null;
}

export interface PublicInvoiceDetail {
  id: string;
  invoice_number: string;
  invoice_date: string | null;
  due_date: string | null;
  payment_terms: string;
  status: string;
  line_items: [string, LineItem[]][];
  note_for_client: string;
  subtotal: string;
  total_markup: string;
  total_discount: string;
  total_amount: string;
  stripe_card_enabled: boolean;
  stripe_ach_enabled: boolean;
  // Shipping
  ship_enabled: boolean;
  ship_fee: string | null;
  ship_date: string | null;
  ship_via: string;
  ship_tracking_number: string;
  contract_instance: PublicContractInstance | null;
  custom_field_values: Record<string, string>;
  pdf_url?: string | null;
}

export interface PublicInvoiceDetailResponse {
  invoice: PublicInvoiceDetail;
  client: PublicClientInfo & { address: string; billing_address: string };
  business: PublicBusinessInfo;
  taxes: Record<string, { name: string; rate: string }>;
}

// Payment types for client portal
export interface PortalPaymentInfo {
  invoice_id?: string;
  invoice_number?: string;
  quote_id?: string;
  quote_number?: string;
  balance_due: string;
  deposit?: string;
  deposit_balance_due?: string;
  stripe_card_enabled: boolean;
  stripe_ach_enabled: boolean;
  available_credit: string;
  stripe_account_id: string;
  has_pending_payment: boolean;
  pending_payment_amount: string;
}

export interface CreatePaymentIntentRequest {
  amount: string;
  payment_method_type: 'card' | 'ach';
  apply_credit?: string;
}

export interface CreatePaymentIntentResponse {
  client_secret: string;
  payment_intent_id: string;
  amount: string;
  credit_applied: string;
}

export interface ConfirmPaymentRequest {
  payment_intent_id: string;
  invoice_id?: string;
  quote_id?: string;
  credit_applied?: string;
}

export interface ApplyCreditRequest {
  invoice_id?: string;
  quote_id?: string;
  amount: string;
}

export interface CreditBalanceResponse {
  available_credit: string;
}

async function publicApiRequest<T>(
  endpoint: string,
  options?: RequestInit,
): Promise<ApiResponse<T>> {
  const url = `${API_BASE_URL}${endpoint}`;

  try {
    const response = await fetch(url, {
      method: options?.method || 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: options?.body,
    });

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.detail || 'An error occurred';
      return { error: errorMessage };
    }

    return { data };
  } catch {
    return { error: 'Network error. Please try again.' };
  }
}

export const publicClientApi = {
  getPortal: (publicId: string) =>
    publicApiRequest<PublicPortalResponse>(`/p/${publicId}/`),

  getInvoices: (publicId: string) =>
    publicApiRequest<PublicInvoicesResponse>(`/p/${publicId}/invoices/`),

  getInvoice: (publicId: string, invoiceId: string) =>
    publicApiRequest<PublicInvoiceDetailResponse>(`/p/${publicId}/invoices/${invoiceId}/`),

  getQuotes: (publicId: string) =>
    publicApiRequest<PublicQuotesResponse>(`/p/${publicId}/quotes/`),

  getQuote: (publicId: string, quoteId: string) =>
    publicApiRequest<PublicQuoteDetailResponse>(`/p/${publicId}/quotes/${quoteId}/`),

  // Payment endpoints
  getCreditBalance: (publicId: string) =>
    publicApiRequest<CreditBalanceResponse>(`/p/${publicId}/credit-balance/`),

  getInvoicePaymentInfo: (publicId: string, invoiceId: string) =>
    publicApiRequest<PortalPaymentInfo>(`/p/${publicId}/invoices/${invoiceId}/payment-info/`),

  createInvoicePaymentIntent: (publicId: string, invoiceId: string, data: CreatePaymentIntentRequest) =>
    publicApiRequest<CreatePaymentIntentResponse>(`/p/${publicId}/invoices/${invoiceId}/create-payment-intent/`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getQuotePaymentInfo: (publicId: string, quoteId: string) =>
    publicApiRequest<PortalPaymentInfo>(`/p/${publicId}/quotes/${quoteId}/payment-info/`),

  createQuotePaymentIntent: (publicId: string, quoteId: string, data: CreatePaymentIntentRequest) =>
    publicApiRequest<CreatePaymentIntentResponse>(`/p/${publicId}/quotes/${quoteId}/create-payment-intent/`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  confirmPayment: (publicId: string, data: ConfirmPaymentRequest) =>
    publicApiRequest<{ success: boolean; payment_id?: string; pending?: boolean }>(`/p/${publicId}/confirm-payment/`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  applyCreditOnly: (publicId: string, data: ApplyCreditRequest) =>
    publicApiRequest<{ success: boolean }>(`/p/${publicId}/apply-credit/`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// Direct document access API (by document ID only, no client ID required)
export const publicDocumentApi = {
  // Invoice
  getInvoice: (invoiceId: string) =>
    publicApiRequest<DirectInvoiceResponse>(`/d/invoice/${invoiceId}/`),

  // Quote
  getQuote: (quoteId: string) =>
    publicApiRequest<DirectQuoteResponse>(`/d/quote/${quoteId}/`),

  approveQuote: (quoteId: string) =>
    publicApiRequest<QuoteActionResponse>(`/d/quote/${quoteId}/approve/`, {
      method: 'POST',
    }),

  declineQuote: (quoteId: string, reason?: string) =>
    publicApiRequest<QuoteActionResponse>(`/d/quote/${quoteId}/decline/`, {
      method: 'POST',
      body: JSON.stringify({ reason: reason || '' }),
    }),

  signQuoteClient: (quoteId: string, signatory_name: string, signature_image: string) =>
    publicApiRequest<QuoteSignClientResponse>(`/d/quote/${quoteId}/sign-client/`, {
      method: 'POST',
      body: JSON.stringify({ signatory_name, signature_image }),
    }),

  signInvoiceClient: (invoiceId: string, signatory_name: string, signature_image: string) =>
    publicApiRequest<InvoiceSignClientResponse>(`/d/invoice/${invoiceId}/sign-client/`, {
      method: 'POST',
      body: JSON.stringify({ signatory_name, signature_image }),
    }),

  // Purchase Order
  getPurchaseOrder: (poId: string) =>
    publicApiRequest<DirectPurchaseOrderResponse>(`/d/purchase-order/${poId}/`),

  // Credit Memo
  getCreditMemo: (cmId: string) =>
    publicApiRequest<DirectCreditMemoResponse>(`/d/credit-memo/${cmId}/`),

  // Client Review
  submitInvoiceReview: (invoiceId: string, rating: number, comments: string) =>
    publicApiRequest<ClientReview>(`/d/invoice/${invoiceId}/review/`, {
      method: 'POST',
      body: JSON.stringify({ rating, comments }),
    }),

  // Legacy Hash Lookup (for old lnk.invoiceowl.com URLs)
  lookupLegacyHash: (hash: string) =>
    publicApiRequest<{ document_type: string; document_id: string; redirect_url: string }>(
      `/d/legacy/${hash}/`
    ),

  // Render HTML (public endpoint, uses same format as documentsApi.renderHtml)
  renderHtml: (request: RenderDocumentRequest) =>
    publicApiRequest<{ html: string }>('/d/render/html/', {
      method: 'POST',
      body: JSON.stringify(request),
    }),

  // Render PDF (public endpoint, returns blob)
  renderPdf: async (request: RenderDocumentRequest): Promise<Blob> => {
    const response = await fetch(`${API_BASE_URL}/d/render/pdf/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to generate PDF' }));
      throw new Error(error.detail || 'Failed to generate PDF');
    }

    return response.blob();
  },

  /**
   * Render document by ID to HTML (single source of truth - backend builds payload).
   * Use this instead of renderHtml for real documents.
   */
  renderByIdHtml: (request: {
    document_id: string;
    document_type: 'invoice' | 'quote' | 'credit_memo' | 'purchase_order' | 'receipt';
    settings?: Record<string, unknown>;
  }) =>
    publicApiRequest<{ html: string }>('/d/render-by-id/html/', {
      method: 'POST',
      body: JSON.stringify(request),
    }),
};

export function saveTokens(tokens: { access: string; refresh: string }) {
  localStorage.setItem('accessToken', tokens.access);
  localStorage.setItem('refreshToken', tokens.refresh);
}

export function clearTokens() {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
}

export function getAccessToken() {
  return localStorage.getItem('accessToken');
}

export interface UploadResponse {
  url: string;
}

export const uploadApi = {
  uploadLogo: async (file: File, isRetry = false): Promise<ApiResponse<UploadResponse>> => {
    const formData = new FormData();
    formData.append('file', file);

    const token = await getValidToken();

    try {
      const response = await fetch(`${API_BASE_URL}/upload/logo/`, {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        // Check for invalid/expired token
        if (data.code === 'token_not_valid' && !isRetry) {
          // Try to refresh token
          if (!isRefreshing) {
            isRefreshing = true;
            refreshPromise = refreshAccessToken();
          }

          const newToken = await refreshPromise;
          isRefreshing = false;
          refreshPromise = null;

          if (newToken) {
            // Retry the upload with new token
            return uploadApi.uploadLogo(file, true);
          }

          // Refresh failed, clear tokens and redirect
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          window.location.href = '/login';
          return { error: 'Session expired. Please login again.' };
        }

        return { error: data.detail || 'Upload failed' };
      }

      return { data };
    } catch {
      return { error: 'Network error. Please try again.' };
    }
  },
};

// =============================================================================
// PUBLIC CLIENT PORTAL - QUOTES
// =============================================================================

export interface PublicQuoteListItem {
  id: string;
  quote_number: string;
  quote_date: string | null;
  due_date: string | null;
  status: string;
  total_amount: string;
}

export interface PublicQuotesResponse {
  quotes: PublicQuoteListItem[];
  client: {
    id: string;
    billing_name: string;
  };
}

export interface PublicQuoteDetail {
  id: string;
  quote_number: string;
  quote_date: string | null;
  due_date: string | null;
  status: string;
  line_items: [string, LineItem[]][];
  note_for_client: string;
  deposit: string;
  subtotal: string;
  total_markup: string;
  total_discount: string;
  total_amount: string;
  stripe_card_enabled: boolean;
  stripe_ach_enabled: boolean;
  ship_enabled: boolean;
  ship_fee: string | null;
  ship_date: string | null;
  ship_via: string;
  ship_tracking_number: string;
  contract_instance: PublicContractInstance | null;
  custom_field_values: Record<string, string>;
  pdf_url?: string | null;
}

export interface PublicQuoteDetailResponse {
  quote: PublicQuoteDetail;
  client: PublicClientInfo & { address: string; billing_address: string };
  business: PublicBusinessInfo;
  taxes: Record<string, { name: string; rate: string }>;
}

// =============================================================================
// DIRECT DOCUMENT ACCESS TYPES
// =============================================================================

export interface PublicBusinessContactInfo {
  name: string;
  email: string;
  phone: string;
  address: string;
  logo: string;
  payment_instructions: string;
}

export interface TemplateConfig {
  template_name: string;
  settings: Record<string, unknown>;
}

// Extended invoice detail with payment info
export interface DirectInvoiceDetail extends PublicInvoiceDetail {
  amount_paid: string;
  balance_due: string;
}

export interface DirectInvoiceResponse {
  invoice: DirectInvoiceDetail;
  client: PublicClientInfo & { address: string; billing_address: string };
  business: PublicBusinessContactInfo;
  template_config: TemplateConfig;
  taxes: Record<string, { name: string; rate: string }>;
  client_reviews_enabled: boolean;
  rendered_html: string | null;
}

// Extended quote detail with payment info
export interface DirectQuoteDetail extends PublicQuoteDetail {
  amount_paid: string;
  balance_due: string;
}

export interface DirectQuoteResponse {
  quote: DirectQuoteDetail;
  client: PublicClientInfo & { address: string; billing_address: string };
  business: PublicBusinessContactInfo;
  template_config: TemplateConfig;
  taxes: Record<string, { name: string; rate: string }>;
  rendered_html: string | null;
}

// Purchase order detail
export interface DirectPurchaseOrderDetail {
  id: string;
  purchase_order_number: string;
  issued_date: string | null;
  status: string;
  line_items: [string, LineItem[]][];
  note_for_vendor: string;
  subtotal: string;
  total_markup: string;
  total_amount: string;
  ship_enabled: boolean;
  ship_fee: string | null;
  ship_date: string | null;
  ship_via: string;
  ship_tracking_number: string;
  custom_field_values: Record<string, string>;
  pdf_url?: string | null;
}

export interface DirectPurchaseOrderResponse {
  purchase_order: DirectPurchaseOrderDetail;
  vendor: (PublicClientInfo & { address: string }) | null;
  business: PublicBusinessContactInfo;
  template_config: TemplateConfig;
  taxes: Record<string, { name: string; rate: string }>;
  rendered_html: string | null;
}

// Credit memo detail
export interface DirectCreditMemoDetail {
  id: string;
  credit_memo_number: string;
  credit_memo_date: string | null;
  status: string;
  line_items: [string, LineItem[]][];
  note_for_client: string;
  subtotal: string;
  total_amount: string;
  custom_field_values: Record<string, string>;
  pdf_url?: string | null;
}

export interface DirectCreditMemoResponse {
  credit_memo: DirectCreditMemoDetail;
  client: (PublicClientInfo & { address: string }) | null;
  business: PublicBusinessContactInfo;
  template_config: TemplateConfig;
  taxes: Record<string, { name: string; rate: string }>;
  rendered_html: string | null;
}

// Not found response (includes business info for contact page)
export interface DocumentNotFoundResponse {
  detail: string;
  business?: PublicBusinessContactInfo;
}

// Quote action responses
export interface QuoteActionResponse {
  success: boolean;
  quote: {
    id: string;
    status: string;
  };
}

export interface QuoteSignClientResponse {
  success: boolean;
  auto_approved: boolean;
  quote: {
    id: string;
    status: string;
    contract_instance: {
      id: string;
      counterparty_signatory: string;
      counterparty_signature_image: string;
      counterparty_signed_at: string | null;
    };
  };
}

export interface InvoiceSignClientResponse {
  success: boolean;
  invoice: {
    id: string;
    status: string;
    contract_instance: {
      id: string;
      counterparty_signatory: string;
      counterparty_signature_image: string;
      counterparty_signed_at: string | null;
    };
  };
}

// =============================================================================
// PAYMENT API
// =============================================================================

export type PaymentMethod = 'check' | 'card' | 'ach' | 'cash' | 'cash_app' | 'paypal' | 'venmo' | 'zelle' | 'credit_memo' | 'stripe' | 'other';
export type PaymentMethodChoice = [PaymentMethod, string];

export interface PaymentAllocation {
  id: string;
  invoice: string | null;
  invoice_number: string | null;
  quote: string | null;
  quote_number: string | null;
  amount: string;
  created_at: string;
}

export type PaymentStatus = 'completed' | 'pending' | 'failed';

export type PaymentType = 'payment' | 'refund';
export type RefundReason = 'requested_by_customer' | 'duplicate' | 'fraudulent' | 'other';

export interface Payment {
  id: string;
  business: string;
  client: string;
  client_name: string;
  credit_memo: string | null;
  // Refund-related fields
  type: PaymentType;
  original_payment: string | null;
  refund_reason: RefundReason | '';
  stripe_refund_id: string;
  stripe_failure_reason: string;
  // Regular fields
  amount: string;
  payment_date: string;
  payment_method: PaymentMethod;
  reference: string;
  notes: string;
  stripe_payment_method: string | null;
  stripe_payment_id: string | null;
  stripe_fee: string | null;
  date_completed: string | null;
  date_failed: string | null;
  status: PaymentStatus;
  allocations: PaymentAllocation[];
  unallocated_amount: string;
  // Refund computed fields
  total_refunded: string;
  refundable_amount: string;
  is_refundable: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreatePaymentAllocation {
  invoice?: string;
  quote?: string;
  amount: string;
}

export interface CreatePaymentData {
  business: string;
  client: string;
  amount: string;
  payment_date: string;
  payment_method: PaymentMethod;
  reference?: string;
  notes?: string;
  allocations?: CreatePaymentAllocation[];
}

export interface RefundData {
  amount: string;
  reason: RefundReason;
  notes?: string;
}

export interface OutstandingInvoice {
  id: string;
  invoice_number: string;
  client: string;
  client_name: string;
  client_address: string;
  invoice_date: string | null;
  due_date: string | null;
  status: InvoiceStatus;
  total_amount: string;
  amount_paid: string;
  balance_due: string;
}

export interface ClientOutstandingResponse {
  client: {
    id: string;
    billing_name: string;
    address: string;
  };
  outstanding_invoices: OutstandingInvoice[];
  total_outstanding: string;
  available_credit: string;
  account_balance: string;
}

export interface OutstandingQuote {
  id: string;
  quote_number: string;
  client: string;
  client_name: string;
  client_address: string;
  quote_date: string | null;
  due_date: string | null;
  status: QuoteStatus;
  total_amount: string;
  deposit: string;
  amount_paid: string;
  balance_due: string;
  deposit_fully_paid: boolean;
}

export interface QuoteOutstandingDepositResponse {
  client: {
    id: string;
    billing_name: string;
    address: string;
  };
  quote: OutstandingQuote;
  deposit_requested: string;
  deposit_collected: string;
  deposit_balance_due: string;
  available_credit: string;
}

export const paymentApi = {
  list: async (businessId?: string, clientId?: string, paymentMethod?: string, startDate?: string, endDate?: string) => {
    const params = new URLSearchParams();
    if (businessId) params.append('business_id', businessId);
    if (clientId) params.append('client_id', clientId);
    if (paymentMethod) params.append('payment_method', paymentMethod);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    return apiRequest<Payment[]>(`/payments/?${params.toString()}`, { method: 'GET' });
  },

  create: async (data: CreatePaymentData) => {
    return apiRequest<Payment>('/payments/', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  get: async (id: string) => {
    return apiRequest<Payment>(`/payments/${id}/`, { method: 'GET' });
  },

  delete: async (id: string) => {
    return apiRequest<void>(`/payments/${id}/`, { method: 'DELETE' });
  },

  getPaymentMethods: () => apiRequest<PaymentMethodChoice[]>('/payment-methods/'),

  getClientOutstanding: (clientId: string) =>
    apiRequest<ClientOutstandingResponse>(`/clients/${clientId}/outstanding-invoices/`),

  getQuoteOutstandingDeposit: (quoteId: string) =>
    apiRequest<QuoteOutstandingDepositResponse>(`/quotes/${quoteId}/outstanding-deposit/`),

  sendReceipt: async (paymentId: string, email?: string) => {
    return apiRequest<{ detail: string }>(`/payments/${paymentId}/send-receipt/`, {
      method: 'POST',
      body: JSON.stringify(email ? { email } : {}),
    });
  },

  getReceiptDownloadUrl: (paymentId: string) => {
    return `${API_BASE_URL}/payments/${paymentId}/download-receipt/`;
  },

  refund: async (paymentId: string, data: RefundData) => {
    return apiRequest<Payment>(`/payments/${paymentId}/refund/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

// =============================================================================
// PURCHASE ORDER API
// =============================================================================

export type PurchaseOrderStatus = 'draft' | 'sent' | 'accepted';

export interface PurchaseOrder {
  id: string;
  purchase_order_number: string;
  status: PurchaseOrderStatus;
  business: string;
  vendor: string | null;
  vendor_name: string;
  vendor_company: string;
  vendor_address: string;
  issued_date: string | null;
  line_items: [string, LineItem[]][];
  markup_dollars: string;
  markup_percent: string;
  markup_applied_to: MarkupAppliedTo;
  // Shipping
  ship_enabled: boolean;
  ship_fee: string | null;
  ship_date: string | null;
  ship_via: string;
  ship_tracking_number: string;
  subtotal: string;
  total_markup: string;
  total_amount: string;
  custom_field_values: Record<string, string>;
  note_for_vendor: string;
  internal_notes: string;
  attachments: Attachment[];
  pdf_url?: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreatePurchaseOrderData {
  business: string;
  vendor?: string;
  issued_date?: string;
  line_items?: [string, LineItem[]][];
  markup_dollars?: number;
  markup_percent?: number;
  markup_applied_to?: MarkupAppliedTo;
  ship_enabled?: boolean;
  ship_fee?: number | null;
  ship_date?: string | null;
  ship_via?: string;
  ship_tracking_number?: string;
  note_for_vendor?: string;
  internal_notes?: string;
  custom_field_values?: Record<string, string>;
}

export const purchaseOrderApi = {
  list: async (
    businessId?: string,
    vendorId?: string,
    status?: string,
    startDate?: string,
    endDate?: string,
    pagination?: PaginationParams
  ) => {
    const params = new URLSearchParams();
    if (businessId) params.append('business_id', businessId);
    if (vendorId) params.append('vendor_id', vendorId);
    if (status && status !== 'all') params.append('status', status);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (pagination?.limit) params.append('limit', pagination.limit.toString());
    if (pagination?.offset !== undefined) params.append('offset', pagination.offset.toString());
    if (pagination?.ordering) params.append('ordering', pagination.ordering);
    const query = params.toString();
    return apiRequest<PaginatedResponse<PurchaseOrder>>(`/purchase-orders/${query ? `?${query}` : ''}`);
  },

  get: async (id: string) => {
    return apiRequest<PurchaseOrder>(`/purchase-orders/${id}/`);
  },

  create: async (data: CreatePurchaseOrderData) => {
    return apiRequest<PurchaseOrder>('/purchase-orders/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  },

  update: async (id: string, data: Partial<CreatePurchaseOrderData>) => {
    return apiRequest<PurchaseOrder>(`/purchase-orders/${id}/`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  },

  updateNumber: async (id: string, purchase_order_number: string) => {
    return apiRequest<PurchaseOrder>(`/purchase-orders/${id}/number/`, {
      method: 'PATCH',
      body: JSON.stringify({ purchase_order_number })
    });
  },

  delete: async (id: string) => {
    return apiRequest<void>(`/purchase-orders/${id}/`, {
      method: 'DELETE',
    });
  },

  send: async (id: string, data: { medium: string; recipient?: string; subject?: string; message?: string }) => {
    return apiRequest<PurchaseOrder>(`/purchase-orders/${id}/send/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  },

  accept: async (id: string) => {
    return apiRequest<PurchaseOrder>(`/purchase-orders/${id}/accept/`, {
      method: 'POST',
    });
  },

  archive: async (id: string) => {
    return apiRequest<PurchaseOrder>(`/purchase-orders/${id}/archive/`, {
      method: 'POST',
    });
  },

  uploadAttachment: async (id: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiRequest<Attachment>(`/purchase-orders/${id}/attachments/`, {
      method: 'POST',
      body: formData,
    });
  },

  deleteAttachment: async (id: string, index: number) => {
    return apiRequest<void>(`/purchase-orders/${id}/attachments/${index}/`, {
      method: 'DELETE',
    });
  },

  getStatusChoices: async () => {
    return apiRequest<Array<{ value: string; label: string }>>(`/purchase-order-statuses/`);
  },

  getSendTemplate: async (poId: string, medium: 'email' | 'sms' = 'email') => {
    return apiRequest<{ subject: string; body: string; is_custom: boolean }>(
      `/purchase-orders/${poId}/send-template/?medium=${medium}`
    );
  },
};

// =============================================================================
// STRIPE CONNECT
// =============================================================================

export interface StripeConnectAccount {
  id: string;
  stripe_account_id: string;
  onboarding_status: 'not_started' | 'in_progress' | 'complete';
  charges_enabled: boolean;
  payouts_enabled: boolean;
  details_submitted: boolean;
  is_fully_onboarded: boolean;
  created_at: string;
  updated_at: string;
}

export interface ConnectStripeResponse {
  onboarding_url: string;
  account_id: string;
  status: string;
}

export const stripeApi = {
  getAccount: (businessId: string) =>
    apiRequest<StripeConnectAccount>(`/businesses/${businessId}/stripe-account/`),

  connect: (businessId: string) =>
    apiRequest<ConnectStripeResponse>(`/businesses/${businessId}/stripe-account/connect/`, {
      method: 'POST',
    }),

  refresh: (businessId: string) =>
    apiRequest<StripeConnectAccount>(`/businesses/${businessId}/stripe-account/refresh/`, {
      method: 'POST',
    }),
};

// =============================================================================
// ACCOUNTING INTEGRATION API
// =============================================================================

export interface AccountingConnection {
  id: string;
  business_id: string;
  business_name: string;
  platform: 'quickbooks' | 'xero' | 'freshbooks';
  platform_display: string;
  status: 'active' | 'expired' | 'disconnected' | 'error';
  company_name: string;
  auto_sync_enabled: boolean;
  accounting_method: 'cash' | 'accrual';
  last_sync_at: string | null;
  last_sync_status: string;
  connected_at: string;
  sync_stats?: {
    synced: number;
    pending: number;
    error: number;
  };
}

export interface AccountingConnectionDetail extends AccountingConnection {
  realm_id: string;
  token_expires_at: string;
  is_token_expired: boolean;
  sync_by_type: {
    [key: string]: {
      total: number;
      synced: number;
      pending: number;
      error: number;
    };
  };
}

export interface AccountingStatus {
  quickbooks: AccountingPlatformStatus | null;
  xero: AccountingPlatformStatus | null;
  freshbooks: AccountingPlatformStatus | null;
}

export interface AccountingPlatformStatus {
  status: string;
  company_name: string;
  last_sync_at: string | null;
  last_sync_status: string;
  auto_sync_enabled: boolean;
  connected_at: string;
}

export interface SyncPreviewChange {
  entity_id: string;
  action: 'create' | 'update' | 'retry';
  display_name: string;
  details: Record<string, string>;
}

export interface SyncPreview {
  total_pending: number;
  by_type: {
    client: SyncPreviewChange[];
    invoice: SyncPreviewChange[];
    credit_memo: SyncPreviewChange[];
    payment: SyncPreviewChange[];
  };
  summary: {
    clients: number;
    invoices: number;
    credit_memos: number;
    payments: number;
  };
}

export interface SyncLogEntry {
  id: string;
  status: 'started' | 'completed' | 'partial' | 'failed';
  items_synced: number;
  items_failed: number;
  items_skipped: number;
  details: {
    by_type?: Record<string, { synced: number; failed: number }>;
    errors?: Array<{ entity_type: string; entity_id: string; display_name: string; error: string }>;
    error?: string;
  };
  started_at: string;
  completed_at: string | null;
  triggered_by: string | null;
}

export interface SyncHistoryResponse {
  total: number;
  limit: number;
  offset: number;
  results: SyncLogEntry[];
}

export interface SyncExecuteResponse {
  sync_log_id: string;
  status: string;
  items_synced: number;
  items_failed: number;
  items_skipped: number;
  details: Record<string, unknown>;
  started_at: string;
  completed_at: string | null;
}

export const accountingApi = {
  // Get status for all platforms for a business
  getStatus: (businessId: string) =>
    apiRequest<AccountingStatus>(`/accounting/status/${businessId}/`),

  // QuickBooks OAuth
  quickbooksConnect: (businessId: string) =>
    apiRequest<{ auth_url: string }>(`/accounting/quickbooks/connect/${businessId}/`, {
      method: 'POST',
    }),

  quickbooksDisconnect: (businessId: string) =>
    apiRequest<{ success: boolean }>(`/accounting/quickbooks/disconnect/${businessId}/`, {
      method: 'POST',
    }),

  // Connection management
  listConnections: () =>
    apiRequest<AccountingConnection[]>('/accounting/connections/'),

  getConnection: (connectionId: string) =>
    apiRequest<AccountingConnectionDetail>(`/accounting/connections/${connectionId}/`),

  updateConnection: (connectionId: string, data: { auto_sync_enabled?: boolean; accounting_method?: 'cash' | 'accrual' }) =>
    apiRequest<{ success: boolean; auto_sync_enabled: boolean; accounting_method: 'cash' | 'accrual' }>(
      `/accounting/connections/${connectionId}/update/`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    ),

  // Sync operations
  getSyncPreview: (connectionId: string, entityTypes?: string[]) => {
    const params = new URLSearchParams();
    if (entityTypes?.length) {
      params.append('entity_types', entityTypes.join(','));
    }
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiRequest<SyncPreview>(`/accounting/connections/${connectionId}/preview/${query}`);
  },

  executeSync: (connectionId: string, entityType?: string, entityId?: string) =>
    apiRequest<SyncExecuteResponse>(`/accounting/connections/${connectionId}/sync/`, {
      method: 'POST',
      body: JSON.stringify(entityType && entityId ? { entity_type: entityType, entity_id: entityId } : {}),
    }),

  getSyncHistory: (connectionId: string, limit = 20, offset = 0) =>
    apiRequest<SyncHistoryResponse>(
      `/accounting/connections/${connectionId}/history/?limit=${limit}&offset=${offset}`
    ),

  // Export
  exportData: (businessId: string, format: 'csv' | 'excel' = 'csv', entityTypes?: string[], startDate?: string, endDate?: string) => {
    const params = new URLSearchParams();
    params.append('export_format', format);
    if (entityTypes?.length) params.append('entity_types', entityTypes.join(','));
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    // For file downloads, we need to handle this differently
    const token = localStorage.getItem('accessToken');
    const url = `${API_BASE_URL}/accounting/export/${businessId}/?${params.toString()}`;

    return fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }).then(async (response) => {
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Export failed');
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
      const filename = filenameMatch ? filenameMatch[1] : `export.${format === 'excel' ? 'xlsx' : 'zip'}`;

      // Trigger download
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);

      return { success: true };
    });
  },
};

// Membership Types
export type MembershipRole = 'admin' | 'full_access' | 'view_only';
export type MembershipStatus = 'joined' | 'invited';

export interface Membership {
  id: string;
  user: string | null;
  business: string;
  role: MembershipRole;
  invited_email: string;
  invited_name: string;
  status: MembershipStatus;
  display_name: string;
  display_email: string;
  created_at: string;
  updated_at: string;
}

export interface InviteMemberData {
  email: string;
  name: string;
  role: MembershipRole;
  business: string;
}

export interface JoinInfo {
  membership_id: string;
  business_name: string;
  business_logo: string;
  invited_name: string;
  invited_email: string;
  role: MembershipRole;
}

export interface JoinResponse {
  user: User;
  tokens: {
    access: string;
    refresh: string;
  };
  business_id: string;
}

// Membership API
export const membershipApi = {
  list: (businessId: string) =>
    apiRequest<Membership[]>(`/memberships/?business_id=${businessId}`),

  create: (data: InviteMemberData) =>
    apiRequest<Membership>('/memberships/', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    apiRequest<void>(`/memberships/${id}/`, {
      method: 'DELETE',
    }),
};

// Public Join API (no auth required)
export const joinApi = {
  getInfo: (membershipId: string) =>
    publicApiRequest<JoinInfo>(`/join/${membershipId}/`),

  accept: (membershipId: string, password: string) =>
    publicApiRequest<JoinResponse>(`/join/${membershipId}/accept/`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    }),
};

// =============================================================================
// REPORTS API
// =============================================================================

export interface ReportsSummary {
  outstanding_invoices: {
    total: string;
    count: number;
  };
  payments_this_month: {
    total: string;
    count: number;
  };
}

export interface AgeBucket {
  amount: string;
  count: number;
}

export interface OutstandingInvoicesSummary {
  total_outstanding: string;
  invoice_count: number;
  buckets: {
    current: AgeBucket;
    '1_30': AgeBucket;
    '31_60': AgeBucket;
    '61_90': AgeBucket;
    '90_plus': AgeBucket;
  };
}

export interface OutstandingInvoiceRow {
  invoice_id: string;
  invoice_number: string;
  client_id: string | null;
  client_name: string;
  client_email: string | null;
  client_phone: string | null;
  invoice_date: string | null;
  due_date: string | null;
  days_overdue: number;
  original_amount: string;
  amount_paid: string;
  amount_due: string;
  age_bucket: string;
}

export interface OutstandingInvoicesGrouped {
  client_id: string | null;
  client_name: string;
  client_email: string | null;
  client_phone: string | null;
  total_outstanding: string;
  invoice_count: number;
  invoices: OutstandingInvoiceRow[];
}

export interface OutstandingInvoicesReport {
  report: string;
  generated_at: string;
  summary: OutstandingInvoicesSummary;
  data: OutstandingInvoiceRow[] | OutstandingInvoicesGrouped[];
}

export interface PaymentMethodBreakdown {
  method: string;
  display: string;
  count: number;
  amount: string;
  percent: string;
}

export interface PaymentsReceivedSummary {
  total_received: string;
  payment_count: number;
  avg_payment: string;
  by_method: PaymentMethodBreakdown[];
  date_range: {
    start: string;
    end: string;
    preset: string | null;
  };
}

export interface PaymentAppliedTo {
  invoice_id?: string;
  invoice_number?: string;
  quote_id?: string;
  quote_number?: string;
  amount: string;
  is_deposit?: boolean;
}

export interface PaymentsReceivedRow {
  payment_id: string;
  payment_date: string;
  client_id: string | null;
  client_name: string;
  payment_method: string;
  payment_method_display: string;
  reference: string;
  amount: string;
  applied_to: PaymentAppliedTo[];
  applied_to_str: string;
  unallocated: string | null;
  notes: string;
  stripe_fee: string | null;
}

export interface PaymentsReceivedReport {
  report: string;
  generated_at: string;
  summary: PaymentsReceivedSummary;
  data: PaymentsReceivedRow[];
}

export type ReportExportFormat = 'csv' | 'xlsx' | 'pdf';

// Revenue Summary Report Types
export interface RevenuePeriod {
  period_label: string;
  period_start: string;
  period_end: string;
  invoice_count: number;
  revenue: string;
  collected: string;
  outstanding: string;
}

export interface RevenueSummarySummary {
  total_revenue: string;
  total_collected: string;
  invoice_count: number;
  avg_invoice: string;
  view: 'week' | 'month';
  date_range: {
    start: string;
    end: string;
    preset: string | null;
  };
}

export interface RevenueSummaryReport {
  report: string;
  generated_at: string;
  summary: RevenueSummarySummary;
  data: RevenuePeriod[];
}

// Invoice History Report Types
export interface InvoiceHistoryRow {
  invoice_id: string;
  invoice_number: string;
  client_id: string | null;
  client_name: string;
  invoice_date: string | null;
  due_date: string | null;
  status: string;
  status_display: string;
  total_amount: string;
  amount_paid: string;
  balance_due: string;
}

export interface InvoiceHistorySummary {
  total_count: number;
  total_amount: string;
  total_paid: string;
  total_balance: string;
  date_range: {
    start: string;
    end: string;
    preset: string | null;
  };
  filters: {
    status: string | null;
    client_id: string | null;
    search: string | null;
  };
  pagination: {
    page: number;
    per_page: number;
    total_pages: number;
    has_next: boolean;
    has_prev: boolean;
  };
}

export interface InvoiceHistoryReport {
  report: string;
  generated_at: string;
  summary: InvoiceHistorySummary;
  data: InvoiceHistoryRow[];
}

// Sales by Client Report Types
export interface ClientSalesRow {
  rank: number;
  client_id: string | null;
  client_name: string;
  invoice_count: number;
  revenue: string;
  amount_paid: string;
  outstanding: string;
  percent_of_total: string;
}

export interface SalesByClientSummary {
  total_revenue: string;
  total_paid: string;
  total_outstanding: string;
  active_clients: number;
  avg_per_client: string;
  date_range: {
    start: string;
    end: string;
    preset: string | null;
  };
}

export interface SalesByClientReport {
  report: string;
  generated_at: string;
  summary: SalesByClientSummary;
  data: ClientSalesRow[];
}

// Sales Tax Summary types
export interface SalesTaxRow {
  tax_id: string;
  tax_name: string;
  tax_rate: string;
  tax_type: 'exclusive' | 'inclusive';
  taxable_sales: string;
  tax_collected: string;
  invoice_count: number;
}

export interface SalesTaxSummary {
  total_taxable_sales: string;
  total_tax_collected: string;
  tax_count: number;
  period_start: string | null;
  period_end: string | null;
  accounting_method: 'cash' | 'accrual';
}

export interface SalesTaxReport {
  report: string;
  generated_at: string;
  summary: SalesTaxSummary;
  data: SalesTaxRow[];
}

export interface SalesTaxTransaction {
  invoice_id: string;
  invoice_number: string;
  date: string | null;
  invoice_date: string | null;
  fully_paid_date: string | null;
  client_name: string;
  status: string;
  tax_id: string;
  tax_name: string;
  tax_rate: string;
  taxable_amount: string;
  tax_amount: string;
}

export const reportsApi = {
  getSummary: (businessId: string) => {
    const params = new URLSearchParams();
    params.append('business_id', businessId);
    return apiRequest<ReportsSummary>(`/reports/summary/?${params.toString()}`);
  },

  getOutstandingInvoices: (businessId: string, groupByClient?: boolean) => {
    const params = new URLSearchParams();
    params.append('business_id', businessId);
    if (groupByClient) params.append('group_by_client', 'true');
    return apiRequest<OutstandingInvoicesReport>(`/reports/outstanding-invoices/?${params.toString()}`);
  },

  getPaymentsReceived: (
    businessId: string,
    range?: string,
    startDate?: string,
    endDate?: string,
    paymentMethods?: string[]
  ) => {
    const params = new URLSearchParams();
    params.append('business_id', businessId);
    if (range) params.append('range', range);
    if (startDate) params.append('start', startDate);
    if (endDate) params.append('end', endDate);
    if (paymentMethods && paymentMethods.length > 0) {
      params.append('payment_method', paymentMethods.join(','));
    }
    return apiRequest<PaymentsReceivedReport>(`/reports/payments/?${params.toString()}`);
  },

  exportOutstandingInvoices: async (
    businessId: string,
    format: ReportExportFormat,
    groupByClient?: boolean
  ) => {
    const params = new URLSearchParams();
    params.append('business_id', businessId);
    params.append('export_format', format);
    if (groupByClient) params.append('group_by_client', 'true');

    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_BASE_URL}/reports/outstanding-invoices/export/?${params.toString()}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Export failed');
    }

    // Get filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `outstanding-invoices.${format}`;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="(.+)"/);
      if (match) filename = match[1];
    }

    const blob = await response.blob();
    downloadBlob(blob, filename);
  },

  exportPaymentsReceived: async (
    businessId: string,
    format: ReportExportFormat,
    range?: string,
    startDate?: string,
    endDate?: string,
    paymentMethods?: string[]
  ) => {
    const params = new URLSearchParams();
    params.append('business_id', businessId);
    params.append('export_format', format);
    if (range) params.append('range', range);
    if (startDate) params.append('start', startDate);
    if (endDate) params.append('end', endDate);
    if (paymentMethods && paymentMethods.length > 0) {
      params.append('payment_method', paymentMethods.join(','));
    }

    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_BASE_URL}/reports/payments/export/?${params.toString()}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Export failed');
    }

    // Get filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `payments-received.${format}`;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="(.+)"/);
      if (match) filename = match[1];
    }

    const blob = await response.blob();
    downloadBlob(blob, filename);
  },

  // Revenue Summary Report
  getRevenueSummary: (
    businessId: string,
    view?: 'week' | 'month',
    range?: string,
    startDate?: string,
    endDate?: string
  ) => {
    const params = new URLSearchParams();
    params.append('business_id', businessId);
    if (view) params.append('view', view);
    if (range) params.append('range', range);
    if (startDate) params.append('start', startDate);
    if (endDate) params.append('end', endDate);
    return apiRequest<RevenueSummaryReport>(`/reports/revenue/?${params.toString()}`);
  },

  exportRevenueSummary: async (
    businessId: string,
    format: ReportExportFormat,
    view?: 'week' | 'month',
    range?: string,
    startDate?: string,
    endDate?: string
  ) => {
    const params = new URLSearchParams();
    params.append('business_id', businessId);
    params.append('export_format', format);
    if (view) params.append('view', view);
    if (range) params.append('range', range);
    if (startDate) params.append('start', startDate);
    if (endDate) params.append('end', endDate);

    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_BASE_URL}/reports/revenue/export/?${params.toString()}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Export failed');
    }

    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `revenue-summary.${format}`;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="(.+)"/);
      if (match) filename = match[1];
    }

    const blob = await response.blob();
    downloadBlob(blob, filename);
  },

  // Invoice History Report
  getInvoiceHistory: (
    businessId: string,
    range?: string,
    startDate?: string,
    endDate?: string,
    status?: string,
    clientId?: string,
    search?: string,
    page?: number,
    perPage?: number
  ) => {
    const params = new URLSearchParams();
    params.append('business_id', businessId);
    if (range) params.append('range', range);
    if (startDate) params.append('start', startDate);
    if (endDate) params.append('end', endDate);
    if (status) params.append('status', status);
    if (clientId) params.append('client', clientId);
    if (search) params.append('search', search);
    if (page) params.append('page', page.toString());
    if (perPage) params.append('per_page', perPage.toString());
    return apiRequest<InvoiceHistoryReport>(`/reports/invoices/?${params.toString()}`);
  },

  exportInvoiceHistory: async (
    businessId: string,
    format: ReportExportFormat,
    range?: string,
    startDate?: string,
    endDate?: string,
    status?: string,
    clientId?: string,
    search?: string
  ) => {
    const params = new URLSearchParams();
    params.append('business_id', businessId);
    params.append('export_format', format);
    if (range) params.append('range', range);
    if (startDate) params.append('start', startDate);
    if (endDate) params.append('end', endDate);
    if (status) params.append('status', status);
    if (clientId) params.append('client', clientId);
    if (search) params.append('search', search);

    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_BASE_URL}/reports/invoices/export/?${params.toString()}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Export failed');
    }

    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `invoice-history.${format}`;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="(.+)"/);
      if (match) filename = match[1];
    }

    const blob = await response.blob();
    downloadBlob(blob, filename);
  },

  // Sales by Client Report
  getSalesByClient: (
    businessId: string,
    range?: string,
    startDate?: string,
    endDate?: string
  ) => {
    const params = new URLSearchParams();
    params.append('business_id', businessId);
    if (range) params.append('range', range);
    if (startDate) params.append('start', startDate);
    if (endDate) params.append('end', endDate);
    return apiRequest<SalesByClientReport>(`/reports/sales-by-client/?${params.toString()}`);
  },

  exportSalesByClient: async (
    businessId: string,
    format: ReportExportFormat,
    range?: string,
    startDate?: string,
    endDate?: string
  ) => {
    const params = new URLSearchParams();
    params.append('business_id', businessId);
    params.append('export_format', format);
    if (range) params.append('range', range);
    if (startDate) params.append('start', startDate);
    if (endDate) params.append('end', endDate);

    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_BASE_URL}/reports/sales-by-client/export/?${params.toString()}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Export failed');
    }

    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `sales-by-client.${format}`;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="(.+)"/);
      if (match) filename = match[1];
    }

    const blob = await response.blob();
    downloadBlob(blob, filename);
  },

  // Sales Tax Summary Report
  getSalesTax: (
    businessId: string,
    range?: string,
    startDate?: string,
    endDate?: string,
    accountingMethod?: 'cash' | 'accrual'
  ) => {
    const params = new URLSearchParams();
    params.append('business_id', businessId);
    if (range) params.append('range', range);
    if (startDate) params.append('start', startDate);
    if (endDate) params.append('end', endDate);
    if (accountingMethod) params.append('accounting_method', accountingMethod);
    return apiRequest<SalesTaxReport>(`/reports/sales-tax/?${params.toString()}`);
  },

  getSalesTaxTransactions: (
    businessId: string,
    range?: string,
    startDate?: string,
    endDate?: string,
    accountingMethod?: 'cash' | 'accrual'
  ) => {
    const params = new URLSearchParams();
    params.append('business_id', businessId);
    if (range) params.append('range', range);
    if (startDate) params.append('start', startDate);
    if (endDate) params.append('end', endDate);
    if (accountingMethod) params.append('accounting_method', accountingMethod);
    return apiRequest<{ transactions: SalesTaxTransaction[]; accounting_method: 'cash' | 'accrual' }>(`/reports/sales-tax/transactions/?${params.toString()}`);
  },

  exportSalesTax: async (
    businessId: string,
    format: ReportExportFormat,
    range?: string,
    startDate?: string,
    endDate?: string,
    detailed?: boolean,
    accountingMethod?: 'cash' | 'accrual'
  ) => {
    const params = new URLSearchParams();
    params.append('business_id', businessId);
    params.append('export_format', format);
    if (range) params.append('range', range);
    if (startDate) params.append('start', startDate);
    if (endDate) params.append('end', endDate);
    if (detailed) params.append('detailed', 'true');
    if (accountingMethod) params.append('accounting_method', accountingMethod);

    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_BASE_URL}/reports/sales-tax/export/?${params.toString()}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Export failed');
    }

    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `sales-tax${detailed ? '-transactions' : ''}.${format}`;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="(.+)"/);
      if (match) filename = match[1];
    }

    const blob = await response.blob();
    downloadBlob(blob, filename);
  },
};

// Billing types
export interface BillingStatus {
  is_billing_owner: boolean;
  billing_admin_email: string | null;
  plan: string | null;
  plan_display_name: string | null;
  subscription_status: string | null;
  billing_interval: string | null;
  is_active: boolean;
  is_trialing: boolean;
  trial_end: string | null;
  days_until_trial_end: number | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  has_stripe_subscription: boolean;
  // Subscription source
  subscription_source: 'stripe' | 'app_store' | 'play_store';
  // Seat info
  free_seats_included: number;
  total_seats_used: number;
  billable_seats: number;
  seat_price_monthly: number;
  owned_businesses_count: number;
  // Legacy subscription info
  is_legacy: boolean;
  migrated_at: string | null;
}

export interface PortalSession {
  portal_url: string;
}

export interface CheckoutSession {
  checkout_url: string;
}

export type OnboardingCheckoutMode = 'setup' | 'payment';

export interface OnboardingSubscription {
  /** 'setup' = free trial: confirm a SetupIntent, then activateTrial. 'payment' = charge now. */
  mode: OnboardingCheckoutMode;
  subscription_id: string | null;
  client_secret: string;
  status: string;
  plan?: string | null;
  interval?: 'monthly' | 'annual' | null;
  trial_days: number;
}

export type CancellationReason =
  | 'too_expensive'
  | 'unused'
  | 'missing_features'
  | 'too_complex'
  | 'low_quality'
  | 'customer_service'
  | 'switched_service'
  | 'other';

export const billingApi = {
  getStatus: (businessId?: string) =>
    apiRequest<BillingStatus>(`/billing/status/${businessId ? `?business_id=${businessId}` : ''}`),

  createPortalSession: (businessId: string) =>
    apiRequest<PortalSession>('/billing/portal/', {
      method: 'POST',
      body: JSON.stringify({ business_id: businessId }),
    }),

  createCheckoutSession: (plan: string, interval: 'monthly' | 'annual', businessId: string) =>
    apiRequest<CheckoutSession>('/billing/checkout/', {
      method: 'POST',
      body: JSON.stringify({ plan, interval, business_id: businessId }),
    }),

  cancelSubscription: (feedback: string, reason: CancellationReason) =>
    apiRequest<{ status: string; cancel_at_period_end: boolean; current_period_end: string | null }>(
      '/billing/cancel/',
      {
        method: 'POST',
        body: JSON.stringify({ feedback, reason }),
      }
    ),

  createSetupIntent: () =>
    apiRequest<{ client_secret: string }>('/billing/setup-intent/', {
      method: 'POST',
    }),

  activateTrial: (plan: string, interval: 'monthly' | 'annual', paymentMethodId: string) =>
    apiRequest<{ status: string; subscription_id: string; subscription_status: string; trial_end: string | null }>(
      '/billing/activate-trial/',
      {
        method: 'POST',
        body: JSON.stringify({ plan, interval, payment_method_id: paymentMethodId }),
      }
    ),

  createOnboardingSubscription: (plan: string, interval: 'monthly' | 'annual', replaceExisting = false) =>
    apiRequest<OnboardingSubscription>('/billing/onboarding-subscription/', {
      method: 'POST',
      body: JSON.stringify({ plan, interval, replace_existing: replaceExisting }),
    }),

  verifyOnboardingSubscription: (subscriptionId: string) =>
    apiRequest<{ status: string; subscription_id: string; subscription_status: string }>(
      '/billing/onboarding-subscription/verify/',
      {
        method: 'POST',
        body: JSON.stringify({ subscription_id: subscriptionId }),
      }
    ),
};

// Transactional Template types
export interface TransactionalTemplateVariable {
  key: string;
  label: string;
}

export interface TransactionalTemplate {
  id: string | null;
  purpose: string;
  purpose_label: string;
  medium: 'email' | 'sms';
  subject: string;
  body: string;
  is_custom: boolean;
  available_variables: TransactionalTemplateVariable[];
}

export const transactionalTemplateApi = {
  list: (businessId: string) =>
    apiRequest<TransactionalTemplate[]>(`/transactional-templates/?business_id=${businessId}`),

  get: (businessId: string, purpose: string, medium: string) =>
    apiRequest<TransactionalTemplate>(
      `/transactional-templates/${purpose}/${medium}/?business_id=${businessId}`
    ),

  update: (businessId: string, purpose: string, medium: string, data: { subject?: string; body: string }) =>
    apiRequest<TransactionalTemplate>(`/transactional-templates/${purpose}/${medium}/`, {
      method: 'PUT',
      body: JSON.stringify({ ...data, business_id: businessId }),
    }),

  resetToDefault: (businessId: string, purpose: string, medium: string) =>
    apiRequest<TransactionalTemplate>(
      `/transactional-templates/${purpose}/${medium}/?business_id=${businessId}`,
      { method: 'DELETE' }
    ),
};

// Helper function to trigger download
function downloadBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

// =============================================================================
// NOTIFICATIONS API
// =============================================================================

export interface Notification {
  id: string;
  info_text: string;
  cta_title: string;
  cta_link: string;
  notification_type: 'action_required' | 'promotion' | 'message';
  notification_document: 'invoice' | 'quote' | 'credit_memo' | 'purchase_order' | 'none';
  show: boolean;
  read: boolean;
  business: string;
  created_at: string;
  updated_at: string;
}

export const notificationApi = {
  list: (businessId: string, limit?: number) => {
    const params = new URLSearchParams();
    params.append('business_id', businessId);
    if (limit) params.append('limit', limit.toString());
    return apiRequest<Notification[]>(`/notifications/?${params.toString()}`);
  },

  getUnreadCount: (businessId: string) =>
    apiRequest<{ count: number }>(`/notifications/unread-count/?business_id=${businessId}`),

  markAllRead: (businessId: string) =>
    apiRequest<{ marked_count: number }>(`/notifications/mark-all-read/?business_id=${businessId}`, {
      method: 'POST',
    }),

  dismiss: (id: string) =>
    apiRequest<Notification>(`/notifications/${id}/dismiss/`, { method: 'POST' }),

  dismissAll: (businessId: string) =>
    apiRequest<{ dismissed_count: number }>(`/notifications/dismiss-all/?business_id=${businessId}`, {
      method: 'POST',
    }),
};
